<template>
  <div class="calculator-container">
    <b-modal v-model="showWizard" :title="calculatorTitle + ' Setup'" hide-footer size="lg" class="wizard-modal" backdrop-class="wizard-backdrop" @hide="onWizardHide">
      <form-wizard ref="wizard" @on-complete="completeWizard" color="#c7522a" subtitle="Provide details about your location—a detailed worksheet will be generated at the end.">
        <tab-content title="Welcome">
          <div class="wizard-intro">
            <h4 class="mb-4">Welcome to the ROI Calculator</h4>
            <p>This quick step-through wizard will help us generate a personalized worksheet showing the potential return on investment for a cashless dining solution.</p>
            <p class="mb-4">There are also other insights that provide helpful information in other areas.</p>
            <p class="mb-4">We'll ask for some basic information about your operations—it only takes a few moments to complete.</p>
            <p class="mb-4">You will also have the option of downloading a PDF report of your ROI analysis form the Analysis section detailing all the calculations and projections.</p>
            <p class="mb-0"><strong>Don't worry about getting everything perfect.</strong> Once you complete this wizard, you'll see a detailed worksheet where you can adjust any of your answers.</p>
          </div>
        </tab-content>
        <tab-content title="Current Sales Information">
          <b-form-group
            label-for="annualCafeteriaSales"
            description="Sum of sales across all participating locations."
          >
            <template #label>
              Total Annual Sales $
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Base for all revenue calculations. Used to derive average transaction value, food cost, and total sales in your financial projections. The more accurate this number, the better your ROI estimates.'" />
            </template>
            <currency-input id="annualCafeteriaSales" v-model="wizardData.annualCafeteriaSales" :options="{ currency: 'USD' }" />
          </b-form-group>
          <b-form-group 
            label-for="numEmployees" 
            description="Total number of accounts, employees, or residents who could potentially use your dining services."
          >
            <template #label>
              Total Possible Accounts #
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Used to calculate participation rate (transactions vs. potential users). This helps estimate the benefit of increasing cashless adoption—a key driver of the projected sales increase.'" />
            </template>
            <b-input id="numEmployees" v-model.number="wizardData.numEmployees" type="number" min="0" />
          </b-form-group>
          <b-form-group 
            label-for="numDailyTransactions" 
            description="Total average number of transactions per day (all payment types combined)."
          >
            <template #label>
              Average # of Daily Transactions
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Core metric for all calculations. Used to derive annual transactions, average ticket value, and credit card fee estimates. Combined with credit card transactions, it determines how much revenue could shift to zero-fee cashless.'" />
            </template>
            <b-input id="numDailyTransactions" v-model.number="wizardData.numDailyTransactions" type="number" min="0" @change="wizardDailyTransactionsChanged()" />
          </b-form-group>
          <b-form-group 
            label-for="creditCardTransactions" 
            description="Number of credit card transactions for the selected period."
          >
            <template #label>
              # Credit Card Transactions
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Drives your credit card fee estimate—a major cost that cashless eliminates. These transactions are converted to daily for the worksheet. The more accurate this number, the better we can show your potential savings.'" />
            </template>
            <b-input id="creditCardTransactions" v-model.number="wizardData.creditCardTransactions" type="number" min="0" />
          </b-form-group>
          <b-form-group 
            label-for="creditCardTransactionsPeriod"
            description="Select whether the transaction count is per day, per week, or per month."
          >
            <template #label>
              Credit Card Transactions Period
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Matches the period you used when counting credit card transactions. The worksheet converts this to a daily rate for fee calculations and projections.'" />
            </template>
            <b-form-radio-group 
              id="creditCardTransactionsPeriod" 
              v-model="wizardData.creditCardTransactionsPeriod"
              @change="wizardCreditCardPeriodChanged()"
              :options="[
                { text: 'Per Day', value: 'daily' },
                { text: 'Per Week', value: 'weekly' },
                { text: 'Per Month', value: 'monthly' }
              ]"
            />
          </b-form-group>
          <b-form-group 
            label-for="avgTransactionValue" 
            description="Average amount per transaction. Leave blank if unknown—we'll calculate it from your sales and transaction data."
          >
            <template #label>
              Average Transaction Value $
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Used to calculate the projected sales increase from traffic and ticket improvements. If blank, we derive it from annual sales ÷ annual transactions. Affects the benefit from cashless.'" />
            </template>
            <currency-input id="avgTransactionValue" v-model="wizardData.avgTransactionValue" :options="{ currency: 'USD' }" />
          </b-form-group>
        </tab-content>
        <tab-content title="Overhead Information">
          <b-form-group 
            label-for="foodCostPrecent" 
            description="Percentage of sales attributed to food costs."
          >
            <template #label>
              Food Cost %
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Subtracted from total sales to calculate gross profit. Industry typical is 28–35%. This directly reduces your net profit in the worksheet.'" />
            </template>
            <b-input id="foodCostPrecent" v-model.number="wizardData.foodCostPrecent" type="number" min="0" max="100" />
          </b-form-group>
          <b-form-group 
            label-for="numFoodserviceStaff" 
            description="Average # staff per shift; how many on the clock at any one time."
          >
            <template #label>
              # Foodservice Staff (Per Shift)
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Combined with wage and hours to calculate annual labor cost—one of the largest expenses. Used for labor cost per transaction and operational efficiency metrics.'" />
            </template>
            <b-input id="numFoodserviceStaff" v-model.number="wizardData.numFoodserviceStaff" type="number" min="0" />
          </b-form-group>
          <b-form-group 
            label-for="averageWage" 
            description="Average hourly wage for full-time foodservice employees."
          >
            <template #label>
              Average Hourly FTE Wage $
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Multiplied by staff count and weekly hours to get annual labor cost. Include benefits if you prefer a fully loaded rate for more accurate projections.'" />
            </template>
            <currency-input id="averageWage" v-model="wizardData.averageWage" :options="{ currency: 'USD' }" />
          </b-form-group>
          <b-form-group 
            label-for="weeklyHours" 
            description="Total hours of operation per week (helps calculate labor and sales per hour)."
          >
            <template #label>
              Weekly Operating Hours
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Used for labor cost (staff × hours × wage) and sales-per-hour metrics. Total operating hours across all days—e.g., 14 hrs/day × 7 days = 98.'" />
            </template>
            <b-input id="weeklyHours" v-model.number="wizardData.weeklyHours" type="number" min="0" />
          </b-form-group>
          <!-- Additional Overhead fields - commented out for now, can be added back later if needed -->
          <!--
          <b-form-group 
            label="Additional Overhead $" 
            label-for="additionalOverhead"
            description="Any additional overhead expenses (utilities, rent, equipment, etc.)."
          >
            <currency-input id="additionalOverhead" v-model="wizardData.additionalOverhead" :options="{ currency: 'USD' }" />
          </b-form-group>
          <b-form-group 
            label="Overhead Period" 
            label-for="additionalOverheadPeriod"
            description="Select whether the overhead amount is daily, monthly, or annual."
          >
            <b-form-radio-group 
              id="additionalOverheadPeriod" 
              v-model="wizardData.additionalOverheadPeriod"
              :options="[
                { text: 'Daily', value: 'daily' },
                { text: 'Monthly', value: 'monthly' },
                { text: 'Annual', value: 'annual' }
              ]"
            />
          </b-form-group>
          -->
        </tab-content>
        <tab-content title="In-House Charge Solution">
          <b-form-group 
            label-for="hasInHouseCharge"
          >
            <template #label>
              Do you have an in-house charge solution?
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Sets default traffic/check increase assumptions: 20%/10% if no system (full benefit from going cashless), 5%/2% if you already have one (incremental improvement). Also affects payback period logic.'" />
            </template>
            <b-form-radio-group 
              id="hasInHouseCharge" 
              v-model="wizardData.hasInHouseCharge"
              @change="wizardHasInHouseChargeChanged()"
              :options="[
                { text: 'Yes', value: true },
                { text: 'No', value: false }
              ]"
            />
          </b-form-group>
          <b-form-group 
            label-for="numDailyChargeTransactions" 
            description="Average number of in-house charge transactions per day (payroll deduction, house accounts, etc.)."
          >
            <template #label>
              # In-House Charge Transactions (Average Daily)
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Payroll deduction and house account transactions. Subtracted from total to estimate credit card transactions. Determines participation rate and the baseline for projected cashless benefit.'" />
            </template>
            <b-input id="numDailyChargeTransactions" v-model.number="wizardData.numDailyChargeTransactions" type="number" min="0" />
          </b-form-group>
          <b-form-group 
            label-for="investment" 
            description="Total 3-year investment for a cashless / in-house charge system (purchase, maintenance, SaaS fees, installation). Required to calculate payback."
          >
            <template #label>
              3-Year Investment $
              <b-icon icon="question-circle-fill" variant="secondary" class="ml-1 wizard-field-tip" v-b-tooltip.hover="'Amortized over 3 years and subtracted from net profit. Drives payback period and ROI calculations. Include hardware, software, implementation, and ongoing fees—whether replacing cash or expanding an existing system.'" />
            </template>
            <currency-input id="investment" v-model="wizardData.investment" :options="{ currency: 'USD' }" />
          </b-form-group>
        </tab-content>
        <template slot="footer" slot-scope="{ activeTabIndex, nextTab, prevTab }">
          <b-button variant="secondary" @click="prevTab" :disabled="activeTabIndex === 0">Previous</b-button>
          <b-button variant="primary" @click="nextTab" v-if="activeTabIndex < 3">Next</b-button>
          <b-button variant="success" @click="completeWizard" v-if="activeTabIndex === 3">Finish</b-button>
        </template>
      </form-wizard>
    </b-modal>

    <div v-if="!showWizard" class="calculator-main">
      <div class="calculator-header">
        <h2 class="calculator-title">{{ calculatorTitle }}</h2>
      </div>
      <b-tabs content-class="calculator-tabs-content" class="main-tabs" v-model="activeTabIndex" @input="onTabChanged">
        <!-- Inputs Tab -->
        <b-tab title="Inputs" active>
          <div class="inputs-container">
            <b-row class="no-gutters">
              <!-- Left Column: Input Fields -->
              <b-col cols="12" lg="7" xl="6">
                <div class="inputs-panel">
                  <div class="input-section">
                    <h4 class="section-title">Organization Information</h4>
                    <div class="form-field">
                      <label for="numEmployees">Accounts / Employees #</label>
                      <b-input id="numEmployees" v-model.number="numEmployees" type="number" min="0" class="input-field" />
                      <span class="field-description">Total accounts in your organization (employees, residents, staff who can do business with you).</span>
                    </div>
                    <div class="form-field">
                      <label for="annualCafeteriaSales">Total Annual Sales $</label>
                      <currency-input id="annualCafeteriaSales" v-model="annualCafeteriaSales" :options="{ currency: 'USD' }" class="input-field" />
                      <span class="field-description">Sum of sales across all participating locations serving these accounts/employees.</span>
                    </div>
                    <div class="form-field">
                      <label for="numDailyTransactions">Daily Transactions #</label>
                      <b-input id="numDailyTransactions" v-model.number="numDailyTransactions" type="number" min="0" @change="dailyTransactionsChanged()" class="input-field" />
                      <span class="field-description">Average total daily transactions (all tender types).</span>
                    </div>
                    <div class="form-field">
                      <label for="creditCardTransactions"># Credit Card Transactions</label>
                      <b-input id="creditCardTransactions" v-model.number="creditCardTransactions" type="number" min="0" class="input-field" />
                      <span class="field-description">Number of credit card transactions for the selected period.</span>
                    </div>
                    <div class="form-field">
                      <label>Credit Card Transactions Period</label>
                      <b-form-radio-group 
                        id="creditCardTransactionsPeriod" 
                        v-model="creditCardTransactionsPeriod"
                        :options="[
                          { text: 'Per Day', value: 'daily' },
                          { text: 'Per Week', value: 'weekly' },
                          { text: 'Per Month', value: 'monthly' }
                        ]"
                        class="overhead-period-radio"
                      />
                      <span class="field-description">Select whether the transaction count is per day, per week, or per month.</span>
                    </div>
                    <div class="form-field">
                      <label for="avgTransactionValue">Average Transaction Value $</label>
                      <currency-input id="avgTransactionValue" v-model="avgTransactionValue" :options="{ currency: 'USD' }" class="input-field" :class="{ 'has-delta': userAvgTransactionValue !== null && Math.abs(calculatedAvgTransactionValue - (userAvgTransactionValue || 0)) > 0.01 }" />
                      <span class="field-description" v-if="!userAvgTransactionValue">
                        Leave blank if unknown—will be calculated from sales and transactions.
                      </span>
                      <div v-if="userAvgTransactionValue !== null && Math.abs(calculatedAvgTransactionValue - userAvgTransactionValue) > 0.01" class="delta-notice">
                        <span class="delta-label">Calculated value:</span>
                        <span class="delta-value calculated">{{ currency(calculatedAvgTransactionValue).format() }}</span>
                        <span class="delta-diff" :class="calculatedAvgTransactionValue > userAvgTransactionValue ? 'positive' : 'negative'">
                          ({{ calculatedAvgTransactionValue > userAvgTransactionValue ? '+' : '' }}{{ currency(calculatedAvgTransactionValue - userAvgTransactionValue).format() }})
                        </span>
                      </div>
                      <span class="field-description" v-else-if="userAvgTransactionValue !== null">
                        Your entered value matches the calculated value.
                      </span>
                    </div>
                  </div>

                  <div class="input-section">
                    <h4 class="section-title">In-House Charge Solution</h4>
                    <div class="form-field">
                      <b-form-checkbox v-model="hasInHouseCharge" id="hasInHouseCharge" class="checkbox-field">
                        Do you have an in-house charge solution?
                      </b-form-checkbox>
                      <span class="field-description">Payroll deduction, house accounts, or other internal charge systems.</span>
                    </div>
                    <div class="form-field">
                      <label for="numDailyChargeTransactions">Daily Charge Transactions #</label>
                      <b-input id="numDailyChargeTransactions" v-model.number="numDailyChargeTransactions" type="number" min="0" @change="dailyChargeTransactionsChanged()" class="input-field" />
                      <span class="field-description">Number of payroll deduction / house account charges (exclude credit/debit cards).</span>
                    </div>
                    <div class="form-field">
                      <label for="participationCount">Estimated Cashless Participation %</label>
                      <b-input id="participationCount" v-model.number="participationCount" type="number" min="0" max="100" @change="participationCountChanged()" class="input-field" />
                      <span class="field-description">Current system participation: Percentage of daily transactions that are payroll deduction / house account charges. Enter any enhanced participation in the "Traffic Increase %" field below.</span>
                    </div>
                    <div class="form-field">
                      <label for="investment">Total System Investment $ (3 years)</label>
                      <currency-input id="investment" v-model="investment" :options="{ currency: 'USD' }" class="input-field" />
                      <span class="field-description">Total 3-year investment (purchase, maintenance, SaaS fees, installation).</span>
                    </div>
                    <div class="form-field">
                      <label for="trafficIncreasePrecent">Estimated Traffic Increase %</label>
                      <b-input id="trafficIncreasePrecent" v-model.number="trafficIncreasePrecent" type="number" min="0" class="input-field" />
                      <span class="field-description" v-if="!hasInHouseCharge">
                        Expected traffic increase with cashless (typically 20% if new). You WILL gain this value.
                      </span>
                      <span class="field-description" v-else>
                        Expected traffic increase with cashless. You already have an in-house system, so this represents potential additional growth beyond what you're already getting.
                      </span>
                    </div>
                    <div class="form-field">
                      <label for="checkIncreasePrecent">Estimated Check Increase %</label>
                      <b-input id="checkIncreasePrecent" v-model.number="checkIncreasePrecent" type="number" min="0" class="input-field" />
                      <span class="field-description" v-if="!hasInHouseCharge">
                        Average spend increase with cashless (10-20% if new). You WILL gain this value.
                      </span>
                      <span class="field-description" v-else>
                        Average spend increase with cashless. You already have an in-house system, so this represents potential additional growth beyond what you're already getting.
                      </span>
                    </div>
                    <div class="no-cashless-note" v-if="!hasInHouseCharge">
                      <p><strong>Without a cashless solution, implementing one would provide:</strong></p>
                      <ul>
                        <li><strong>≥20% sales lift</strong> — traffic &amp; ticket growth applied to your full dining volume (cash + card), not only charge transactions</li>
                        <li><strong>~20% labor reduction</strong> — faster checkout vs. cash lines</li>
                        <li><strong>Cash shrinkage &amp; closeout relief</strong> — less cash on hand means less shrinkage and lighter shift-closeout work</li>
                      </ul>
                    </div>
                  </div>

                  <div class="input-section">
                    <h4 class="section-title">Costs & Overhead</h4>
                    <div class="form-field">
                      <label for="weeklyHours">Weekly Operating Hours</label>
                      <b-input id="weeklyHours" v-model.number="weeklyHours" type="number" min="0" class="input-field" />
                      <span class="field-description">Total hours of operation per week (helps calculate labor and sales per hour).</span>
                    </div>
                    <div class="form-field">
                      <label for="numFoodserviceStaff"># Foodservice Staff (Per Shift)</label>
                      <b-input id="numFoodserviceStaff" v-model.number="numFoodserviceStaff" type="number" min="0" class="input-field" />
                      <span class="field-description">Average # staff per shift; how many on the clock at any one time.</span>
                    </div>
                    <div class="form-field">
                      <label for="averageWage">Average Hourly Wage $</label>
                      <currency-input id="averageWage" v-model="averageWage" :options="{ currency: 'USD' }" class="input-field" />
                      <span class="field-description">Average # staff per shift; how many on the clock at any one time.</span>
                    </div>
                    <div class="form-field">
                      <label for="foodCostPrecent">Food Cost %</label>
                      <b-input id="foodCostPrecent" v-model.number="foodCostPrecent" type="number" min="0" max="100" class="input-field" />
                      <span class="field-description">Percentage of sales attributed to food costs.</span>
                    </div>
                    <div class="form-field">
                      <label for="additionalOverhead">Additional Overhead $</label>
                      <currency-input id="additionalOverhead" v-model="additionalOverhead" :options="{ currency: 'USD' }" class="input-field" />
                      <span class="field-description">Other operating expenses (utilities, rent, equipment, etc.). Add your estimates here for more accurate projections.</span>
                    </div>
                    <div class="form-field">
                      <label>Overhead Period</label>
                      <b-form-radio-group 
                        id="additionalOverheadPeriod" 
                        v-model="additionalOverheadPeriod"
                        :options="[
                          { text: 'Daily', value: 'daily' },
                          { text: 'Monthly', value: 'monthly' },
                          { text: 'Annual', value: 'annual' }
                        ]"
                        class="overhead-period-radio"
                      />
                      <span class="field-description">Select whether the overhead amount is daily, monthly, or annual.</span>
                    </div>
                  </div>

                  <div class="actions-bar">
                    <b-button variant="primary" @click="restartWizard">Start Over</b-button>
                  </div>
                </div>
              </b-col>

              <!-- Right Column: Key Financial Metrics -->
              <b-col cols="12" lg="5" xl="6">
                <div class="metrics-panel">
                  <div class="key-results-section">
                    <h4 class="section-title">Key Financial Metrics</h4>
                    <div class="financial-statement">
                      <!-- Starting Amount -->
                      <div class="financial-line financial-start">
                        <div class="financial-label">Total Annual Sales</div>
                        <div class="financial-value">{{ currency(annualCafeteriaSales).format() }}</div>
                      </div>
                      
                      <!-- Sales Increase (addition) -->
                      <div class="financial-line financial-add">
                        <div class="financial-label">Added Annual Sales Increase</div>
                        <div class="financial-value highlight">{{ currency(avgAnnualIncreaseSales || 0).format() }}</div>
                        <div class="financial-note" v-if="!hasInHouseCharge">
                          Benefit from going cashless: traffic &amp; ticket lift on full dining volume (cash + card)
                        </div>
                        <div class="financial-note" v-else>
                          Benefit from cashless: traffic &amp; ticket improvements on charge transactions
                        </div>
                      </div>
                      
                      <!-- Costs (subtractions) -->
                      <div class="financial-line financial-cost">
                        <div class="financial-label">Less: Food Cost</div>
                        <div class="financial-value">{{ currency((annualCafeteriaSales + avgAnnualIncreaseSales) * (foodCostPrecent / 100)).format() }}</div>
                        <div class="financial-note">{{ foodCostPrecent }}% of total sales</div>
                      </div>
                      
                      <div class="financial-line financial-cost">
                        <div class="financial-label">Less: Labor Cost</div>
                        <div class="financial-value">{{ currency(effectiveAnnualLaborCost).format() }}</div>
                        <div class="financial-note" v-if="!hasInHouseCharge && nonCashlessLaborSavings > 0">
                          Includes ~20% savings from faster cashless checkout ({{ currency(nonCashlessLaborSavings).format() }} saved)
                        </div>
                        <div class="financial-note" v-else-if="numFoodserviceStaff > 0 && weeklyHours > 0">
                          {{ numFoodserviceStaff }} staff × {{ weeklyHours }} hrs/week × ${{ currency(averageWage).format() }}/hr
                        </div>
                        <div class="financial-note" v-else>
                          {{ weeklyHours }} hrs/week × ${{ currency(averageWage).format() }}/hr
                        </div>
                      </div>
                      
                      <div class="financial-line financial-add" v-if="!hasInHouseCharge && nonCashlessCashHandlingSavings > 0">
                        <div class="financial-label">Plus: Cash Handling &amp; Shrinkage Savings</div>
                        <div class="financial-value highlight">{{ currency(nonCashlessCashHandlingSavings).format() }}</div>
                        <div class="financial-note">
                          ~2% of estimated cash sales (shrinkage + shift-closeout burden on {{ currency(estimatedCashSalesAnnual).format() }} cash volume)
                        </div>
                      </div>
                      
                      <div class="financial-line financial-cost">
                        <div class="financial-label">Less: Credit Card Fees</div>
                        <div class="financial-value">{{ currency(annualCreditCardFees).format() }}</div>
                        <div class="financial-note">Potential savings with in-house charge</div>
                      </div>
                      
                      <div class="financial-line financial-cost" v-if="annualAdditionalOverhead > 0">
                        <div class="financial-label">Less: Additional Overhead</div>
                        <div class="financial-value">{{ currency(annualAdditionalOverhead).format() }}</div>
                        <div class="financial-note">Utilities, rent, equipment—add in worksheet for accuracy</div>
                      </div>
                      
                      <div class="financial-line financial-cost" v-if="annualSystemInvestment > 0">
                        <div class="financial-label">Less: System Investment</div>
                        <div class="financial-value">{{ currency(annualSystemInvestment).format() }}</div>
                        <div class="financial-note">Annual portion (3-year total / 3)</div>
                      </div>
                      
                      <!-- Divider line -->
                      <div class="financial-divider"></div>
                      
                      <!-- Result -->
                      <div class="financial-line financial-result">
                        <div class="financial-label">Net Profit</div>
                        <div class="financial-value">{{ currency(Math.max(0, netProfit)).format() }}</div>
                        <div class="financial-note">After all costs</div>
                      </div>
                      
                      <!-- Additional metrics (separate section) -->
                      <div class="financial-divider" style="margin-top: 2rem;"></div>
                      
                      <div class="financial-line financial-metric">
                        <div class="financial-label">Payback Period</div>
                        <div class="financial-value highlight">{{ paybackDisplay }}</div>
                        <div class="financial-note">{{ paybackNote }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="chart-card-small">
                    <h5>Cost Breakdown</h5>
                    <pie-chart ref="costPieChart" :data="costBreakdownChartData" :options="pieChartOptions" />
                  </div>

                  <div class="savings-summary">
                    <h5>Potential Savings</h5>
                    <div class="savings-item">
                      <span class="savings-label">Credit Card Fee Savings:</span>
                      <span class="savings-value">{{ currency(annualCreditCardFees).format() }}</span>
                    </div>
                    <div class="savings-item" v-if="!hasInHouseCharge && nonCashlessLaborSavings > 0">
                      <span class="savings-label">Labor Savings (faster checkout):</span>
                      <span class="savings-value">{{ currency(nonCashlessLaborSavings).format() }}</span>
                    </div>
                    <div class="savings-item" v-if="!hasInHouseCharge && nonCashlessCashHandlingSavings > 0">
                      <span class="savings-label">Cash Shrinkage &amp; Closeout:</span>
                      <span class="savings-value">{{ currency(nonCashlessCashHandlingSavings).format() }}</span>
                    </div>
                    <div class="savings-item">
                      <span class="savings-label">Net Annual Profit Increase:</span>
                      <span class="savings-value highlight">{{ currency(avgNetAnnualIncreaseSales).format() }}</span>
                    </div>
                    <div class="savings-note" v-if="!hasInHouseCharge">
                      Going cashless lifts full dining volume, reduces cash-line labor, and cuts shrinkage / shift-closeout burden—especially when most transactions are still cash.
                    </div>
                    <div class="savings-note" v-else>
                      Converting credit card transactions to in-house charge eliminates processing fees and increases profitability.
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>
        </b-tab>

        <!-- Analysis Tab -->
        <b-tab title="Analysis">
          <div class="analysis-container">
            <!-- Executive Summary -->
            <div class="analysis-section executive-summary">
              <h3 class="section-header">Executive Summary</h3>
              <p class="section-disclaimer">
                Net profit figures are based on the information you provided. Your actual results will vary. 
                The "Added Annual Sales Increase" represents the <strong>benefit</strong> that cashless brings—incremental revenue from traffic and ticket improvements. 
                Other revenue is already reflected in your base sales. Use the <strong>Additional Overhead</strong> field in the worksheet to estimate utilities, rent, and other expenses for more accurate projections.
              </p>
              <!-- Hero callout: Net Profit Margin -->
              <div class="hero-callout">
                <div class="hero-callout-label">Net Profit Margin</div>
                <div class="hero-callout-value">{{ grossSales > 0 ? ((netProfit / grossSales) * 100).toFixed(2) : 'N/A' }}%</div>
                <div class="hero-callout-note">Your strongest metric — healthy margin indicates efficient operations</div>
              </div>
              <b-row>
                <b-col cols="12" md="4">
                  <div class="summary-metric">
                    <div class="metric-label">Investment</div>
                    <div class="metric-value">{{ currency(investment).format() }}</div>
                    <div class="metric-note">3-year total</div>
                  </div>
                </b-col>
                <b-col cols="12" md="4">
                  <div class="summary-metric">
                    <div class="metric-label">Payback Period</div>
                    <div class="metric-value highlight">{{ paybackDisplay }}</div>
                    <div class="metric-note">{{ paybackNote }}</div>
                  </div>
                </b-col>
                <b-col cols="12" md="4">
                  <div class="summary-metric">
                    <div class="metric-label">5-Year Net Profit</div>
                    <div class="metric-value highlight">{{ currency(fiveYearNetBenefit).format() }}</div>
                    <div class="metric-note">Cumulative after investment. Results vary based on inputs.</div>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- 5-Year Financial Forecast -->
            <div class="analysis-section">
              <h3 class="section-header">5-Year Financial Forecast</h3>
              <p class="section-description">Projected financial performance based on current inputs and system improvements. Net profit is estimated from the information provided—your actual results will vary.</p>
              <div class="chart-card">
                <line-chart ref="fiveYearForecastChart" :data="fiveYearForecastData" :options="forecastChartOptions" />
              </div>
              <div class="forecast-table-container">
                <table class="forecast-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Total Sales</th>
                      <th>Net Profit</th>
                      <th>Cumulative Net</th>
                      <th>ROI %</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(year, index) in fiveYearForecast" :key="index">
                      <td>{{ year.year }}</td>
                      <td>{{ currency(year.totalSales).format() }}</td>
                      <td>{{ currency(year.netProfit).format() }}</td>
                      <td>{{ currency(year.cumulativeNet).format() }}</td>
                      <td :class="{ 'positive': year.roi > 0, 'negative': year.roi < 0 }">
                        {{ year.roi.toFixed(1) }}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Sensitivity Analysis: The Power of Small Changes (HERO) -->
            <div class="analysis-section sensitivity-hero">
              <h3 class="section-header">Sensitivity Analysis: The Power of Small Changes</h3>
              <p class="section-description">Small operational improvements yield significant financial impact. This is your upside opportunity—minor adjustments that compound over time.</p>
              <div class="chart-card hero-chart">
                <h4>Revenue Impact Scenarios</h4>
                <bar-chart ref="sensitivityChart" :data="sensitivityAnalysisData" :options="sensitivityChartOptions" />
              </div>
              <b-row>
                <b-col cols="12" class="mt-3">
                  <div class="scenario-table-container">
                    <table class="scenario-table">
                      <thead>
                        <tr>
                          <th>Scenario</th>
                          <th>Annual Impact</th>
                          <th>5-Year Impact</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Current Projection</strong></td>
                          <td>{{ currency(avgNetAnnualIncreaseSales).format() }}</td>
                          <td>{{ currency(avgNetAnnualIncreaseSales * 5).format() }}</td>
                        </tr>
                        <tr>
                          <td>+5% Avg Ticket</td>
                          <td>{{ currency(sensitivityScenarios.ticket5.annual).format() }}</td>
                          <td>{{ currency(sensitivityScenarios.ticket5.fiveYear).format() }}</td>
                        </tr>
                        <tr>
                          <td>+10% Participation</td>
                          <td>{{ currency(sensitivityScenarios.participation10.annual).format() }}</td>
                          <td>{{ currency(sensitivityScenarios.participation10.fiveYear).format() }}</td>
                        </tr>
                        <tr>
                          <td>+5% Ticket + 10% Participation</td>
                          <td>{{ currency(sensitivityScenarios.combined.annual).format() }}</td>
                          <td>{{ currency(sensitivityScenarios.combined.fiveYear).format() }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Operational Efficiency Metrics -->
            <div class="analysis-section">
              <h3 class="section-header">Operational Efficiency & Strategic Benefits</h3>
              <b-row>
                <b-col cols="12" md="6">
                  <div class="efficiency-card">
                    <h4>Current State</h4>
                    <table class="efficiency-table">
                      <tr>
                        <td>Sales per Hour</td>
                        <td>{{ currency(salesPerHour).format() }}</td>
                      </tr>
                      <tr>
                        <td>Labor Cost per Transaction</td>
                        <td>{{ currency(annualLaborCost / totalAnnualTransactions).format() }}</td>
                      </tr>
                      <tr>
                        <td>Credit Card Fee Rate</td>
                        <td>{{ ((annualCreditCardFees / (numDailyCreditCardTransactions * 365 * avgDailySale)) * 100).toFixed(2) }}%</td>
                      </tr>
                      <tr class="metric-note-row">
                        <td colspan="2">Effective rate (2.9% + $0.30/txn). The flat fee raises the effective % on smaller tickets.</td>
                      </tr>
                      <tr>
                        <td>Net Profit Margin</td>
                        <td>{{ ((netProfit / (annualCafeteriaSales + avgAnnualIncreaseSales)) * 100).toFixed(2) }}%</td>
                      </tr>
                    </table>
                  </div>
                </b-col>
                <b-col cols="12" md="6">
                  <div class="efficiency-card">
                    <h4>System Investment Benefits</h4>
                    <ul class="benefits-list">
                      <li><strong>Process Improvements:</strong> Automated workflows reduce manual intervention and errors</li>
                      <li><strong>Reduced Maintenance:</strong> Cloud-based system eliminates on-premise infrastructure costs</li>
                      <li><strong>Faster Operations:</strong> Streamlined payment processing increases throughput</li>
                      <li><strong>Infrastructure Efficiency:</strong> Unified platform reduces IT complexity and support costs</li>
                      <li><strong>Zero Transaction Fees:</strong> {{ currency(annualCreditCardFees).format() }} annual savings vs. credit card processing</li>
                      <li><strong>Scalability:</strong> Enterprise-ready platform supports growth without proportional cost increases</li>
                    </ul>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Labor Analysis -->
            <div class="analysis-section">
              <h3 class="section-header">Labor Analysis</h3>
              <p class="section-description">Industry benchmarks for foodservice labor costs. Your position vs. healthy ranges:</p>
              <!-- Traffic light: Labor Cost % vs benchmarks -->
              <div class="labor-traffic-light" :class="laborCostPrecent <= 35 ? 'status-healthy' : laborCostPrecent <= 40 ? 'status-borderline' : 'status-review'">
                <div class="traffic-light-value">Labor Cost: {{ laborCostPrecent.toFixed(1) }}%</div>
                <div class="traffic-light-status">{{ laborCostPrecent <= 35 ? 'Healthy' : laborCostPrecent <= 40 ? 'Borderline' : 'Review' }}</div>
                <div class="traffic-light-note">Industry target: 25–35% (QSR/Casual). {{ primeCostPrecent > 60 ? 'Prime Cost >60% — bold action recommended.' : 'Prime Cost &lt;60% target.' }}</div>
              </div>
              <b-row>
                <b-col cols="12" md="6">
                  <div class="efficiency-card">
                    <h4>Your Current Metrics</h4>
                    <table class="efficiency-table">
                      <tr>
                        <td>Labor Cost %</td>
                        <td :class="{ 'labor-warning': laborCostPrecent >= 40, 'labor-good': laborCostPrecent > 0 && laborCostPrecent <= 35 }">{{ laborCostPrecent.toFixed(1) }}%</td>
                      </tr>
                      <tr>
                        <td>Food Cost (COGS) %</td>
                        <td>{{ foodCostPrecent }}%</td>
                      </tr>
                      <tr>
                        <td>Prime Cost (Labor + COGS)</td>
                        <td>{{ currency(primeCost).format() }}</td>
                      </tr>
                      <tr>
                        <td>Prime Cost %</td>
                        <td :class="{ 'labor-warning': primeCostPrecent > 60 }">{{ primeCostPrecent.toFixed(1) }}%</td>
                      </tr>
                    </table>
                    <p class="labor-note mt-2 mb-0">
                      <strong>Formula:</strong> Labor Cost % = (Total Labor ÷ Gross Sales) × 100
                    </p>
                  </div>
                </b-col>
                <b-col cols="12" md="6">
                  <div class="efficiency-card">
                    <h4>Industry Benchmarks</h4>
                    <table class="efficiency-table labor-benchmarks">
                      <thead>
                        <tr>
                          <th>Service Type</th>
                          <th>Target Labor %</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Quick Service (QSR)</td>
                          <td>25% – 30%</td>
                        </tr>
                        <tr>
                          <td>Casual Dining</td>
                          <td>30% – 35%</td>
                        </tr>
                        <tr>
                          <td>Fine Dining</td>
                          <td>35% – 40%</td>
                        </tr>
                      </tbody>
                    </table>
                    <h5 class="mt-3 mb-2">Key Factors</h5>
                    <ul class="labor-factors-list">
                      <li><strong>Prime Cost Rule:</strong> Labor + COGS should stay below 60%. High food cost? Lean on labor.</li>
                      <li><strong>Full Picture:</strong> Include salaries, taxes, benefits, and insurance in labor costs.</li>
                      <li><strong>Productivity Matters:</strong> A low labor % with poor service is "saving" your way out of business.</li>
                    </ul>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Investment ROI Breakdown -->
            <div class="analysis-section">
              <h3 class="section-header">Investment ROI Analysis</h3>
              <b-row>
                <b-col cols="12" md="6">
                  <div class="chart-card">
                    <h4>Cumulative Cash Flow</h4>
                    <line-chart ref="cashFlowChart" :data="cumulativeCashFlowData" :options="cashFlowChartOptions" />
                  </div>
                </b-col>
                <b-col cols="12" md="6">
                  <div class="roi-breakdown-card">
                    <h4>ROI Metrics</h4>
                    <table class="roi-table">
                      <tr>
                        <td>3-Year Investment</td>
                        <td class="value">{{ currency(investment).format() }}</td>
                      </tr>
                      <tr>
                        <td>Annual Investment (Year 1-3)</td>
                        <td class="value">{{ currency(annualSystemInvestment).format() }}</td>
                      </tr>
                      <tr>
                        <td>Annual Sales Increase</td>
                        <td class="value positive">{{ currency(avgAnnualIncreaseSales).format() }}</td>
                      </tr>
                      <tr>
                        <td>Annual Net Profit Increase</td>
                        <td class="value positive">{{ currency(avgNetAnnualIncreaseSales).format() }}</td>
                      </tr>
                      <tr>
                        <td>Credit Card Fee Savings</td>
                        <td class="value positive">{{ currency(annualCreditCardFees).format() }}</td>
                      </tr>
                      <tr class="total-row">
                        <td><strong>Total Annual Benefit</strong></td>
                        <td class="value highlight"><strong>{{ currency(avgNetAnnualIncreaseSales + annualCreditCardFees).format() }}</strong></td>
                      </tr>
                      <tr>
                        <td>Payback Period</td>
                        <td class="value highlight"><strong>{{ paybackDisplay }}</strong></td>
                      </tr>
                      <tr>
                        <td>3-Year ROI</td>
                        <td class="value highlight"><strong>{{ threeYearROI.toFixed(1) }}%</strong></td>
                      </tr>
                      <tr>
                        <td>5-Year ROI</td>
                        <td class="value highlight"><strong>{{ fiveYearROI.toFixed(1) }}%</strong></td>
                      </tr>
                    </table>
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Strategic Recommendations -->
            <div class="analysis-section">
              <h3 class="section-header">Strategic Recommendations</h3>
              <div class="recommendations-grid">
                <div class="recommendation-card">
                  <h4>Immediate Actions</h4>
                  <ul>
                    <li v-if="participationCount < 50">Increase participation from {{ participationCount }}% to 60%+ to unlock additional revenue potential</li>
                    <li v-if="checkIncreasePrecent < 10">Implement upselling strategies to increase average ticket by 5-10%</li>
                    <li>Leverage zero-fee cashless transactions to reduce credit card processing costs</li>
                    <li>Utilize automated workflows to reduce labor overhead{{ laborCostPrecent >= 35 ? ' (your labor cost supports this)' : '' }}</li>
                  </ul>
                </div>
                <div class="recommendation-card">
                  <h4>Long-Term Strategy</h4>
                  <ul>
                    <li>Focus on incremental improvements: small changes compound significantly over 5 years</li>
                    <li>Monitor participation rates and adjust marketing/engagement strategies accordingly</li>
                    <li>Optimize menu pricing to balance volume and margin</li>
                    <li>Leverage cloud-based infrastructure for scalability and reduced maintenance costs</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <!-- Download PDF Section -->
            <div class="analysis-section">
              <div class="download-pdf-section">
                <h3 class="section-header">Download Your ROI Report</h3>
                <p class="section-description">Get a comprehensive PDF report of your ROI analysis to share with your team or stakeholders.</p>
                <b-button variant="primary" size="lg" @click="showEmailModal = true" class="download-pdf-btn">
                  <i class="fas fa-download"></i> Download PDF Report
                </b-button>
              </div>
            </div>
            
            <newcare-cta
              title="Ready to Automate These Savings?"
              :message="newcareCtaMessage"
              button-text="Explore Newcare Solutions"
              newcare-url="https://www.newcareinternational.com/cashless-payroll-deduction/"
            />
          </div>
        </b-tab>
      </b-tabs>
    </div>
    
    <!-- Email Capture Modal for PDF Download -->
    <b-modal
      id="email-capture-modal"
      v-model="showEmailModal"
      title="Download Your ROI Report"
      hide-footer
      centered
      size="md"
    >
      <template #default>
        <p class="mb-4">This information personalizes your PDF report specifically for you. Please provide the following to download your ROI analysis.</p>
        <b-form @submit.prevent="handlePdfDownload">
          <!-- Honeypot: hidden from humans, bots often fill it. If filled, treat as bot. -->
          <div class="honeypot-field" aria-hidden="true">
            <label for="pdf-num-employees">Number of employees</label>
            <input id="pdf-num-employees" v-model="pdfUserData.numEmployeesHoneypot" type="text" name="number_of_employees" tabindex="-1" autocomplete="off" />
          </div>
          <b-form-group label="Company" label-for="pdf-company">
            <b-form-input
              id="pdf-company"
              v-model="pdfUserData.company"
              type="text"
              placeholder="Enter your company or organization name"
            />
          </b-form-group>
          <b-form-group label="Name" label-for="pdf-name">
            <b-form-input
              id="pdf-name"
              v-model="pdfUserData.name"
              type="text"
              required
              placeholder="Enter your name"
              :state="pdfUserData.name ? null : false"
            />
          </b-form-group>
          <b-form-group label="Email Address" label-for="pdf-email">
            <b-form-input
              id="pdf-email"
              v-model="pdfUserData.email"
              type="email"
              required
              placeholder="Enter your email address"
              :state="pdfUserData.email && isValidEmail(pdfUserData.email) ? null : false"
            />
          </b-form-group>
          <div class="d-flex justify-content-end mt-4">
            <b-button variant="secondary" @click="showEmailModal = false" class="mr-2">Cancel</b-button>
            <b-button
              variant="primary"
              type="submit"
              :disabled="!pdfUserData.name || !pdfUserData.email || !isValidEmail(pdfUserData.email) || isGeneratingPdf"
            >
              <span v-if="isGeneratingPdf">
                <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Generating...
              </span>
              <span v-else>Download PDF</span>
            </b-button>
          </div>
        </b-form>
      </template>
    </b-modal>
  </div>
</template>

<script>
import CurrencyInput from "./CurrencyInput.vue";
import NewcareCTA from "./NewcareCTA.vue";
import Currency from "currency.js";
import { Line, Bar, Pie } from "vue-chartjs";
import Chart from "chart.js";

export default {
  name: "HealthcareROI",
  props: {
    calculatorTitle: { type: String, default: "Calculator Title Here" },
    calculatorDescription: { type: String, default: "Your description here" },
  },
  components: {
    CurrencyInput,
    NewcareCTA,
    'newcare-cta': NewcareCTA, // Explicit kebab-case registration
    LineChart: Line,
    BarChart: Bar,
    PieChart: Pie,
  },
  data() {
    return {
      // Start with wizard to capture initial data
      showWizard: true,
      wizardData: {
        // Step 1: Current Sales Information
        annualCafeteriaSales: 1000000,
        numEmployees: 500,
        numDailyTransactions: 200, // Total average daily transactions
        creditCardTransactions: null, // Will be calculated as 50% of daily transactions in weekly terms
        creditCardTransactionsPeriod: 'weekly', // 'daily', 'weekly', or 'monthly'
        avgTransactionValue: null, // Optional - leave blank if unknown
        
        // Step 2: Overhead Information
        foodCostPrecent: 30,
        numFoodserviceStaff: 4,
        averageWage: 15,
        weeklyHours: 98, // Hours of operation per week (14 hrs/day × 7 days, e.g., 6am-8pm)
        
        // Step 3: In-house charge solution
        hasInHouseCharge: false,
        numDailyChargeTransactions: 0,
        investment: 0,
        
        // Projections (for calculator form)
        checkIncreasePrecent: 10,
        trafficIncreasePrecent: 20,
        
      // Additional overhead
      additionalOverhead: 0,
      additionalOverheadPeriod: 'annual', // 'daily', 'monthly', or 'annual'
      },
      // Tab management
      activeTabIndex: 0, // 0 = Inputs, 1 = Analysis
      // Raw data from wizard
      annualCafeteriaSales: 1000000,
      numEmployees: 500,
      creditCardTransactions: null, // Will be calculated as 50% of daily transactions in weekly terms
      creditCardTransactionsPeriod: 'weekly', // 'daily', 'weekly', or 'monthly'
      avgTransactionValue: null,
      // Preserve what the user originally typed for average ticket so we can
      // compare it to the model-calculated value later in the analysis.
      userAvgTransactionValue: null,
      foodCostPrecent: 30,
      numFoodserviceStaff: 4,
      averageWage: 15,
      weeklyHours: 98, // Hours of operation per week
      hasInHouseCharge: false,
      numDailyChargeTransactions: 0,
      investment: 0,
      additionalOverhead: 0,
      additionalOverheadPeriod: 'annual', // 'daily', 'monthly', or 'annual'
      checkIncreasePrecent: 10,
      trafficIncreasePrecent: 20,
      // PDF Download
      showEmailModal: false,
      pdfUserData: {
        company: '',
        name: '',
        email: '',
        numEmployeesHoneypot: ''
      },
      isGeneratingPdf: false,
      
      // Additional fields
      numFoodserviceStaff: 10,
      
      // Calculated values (reactive)
      numDailyTransactions: 100, // Will be calculated from credit card + charge transactions
      participationCount: 0, // Will be calculated
    };
  },
  computed: {
    // Average ticket based on wizard inputs (used on setup modal)
    wizardAvgDailySale() {
      // If avg transaction value provided (not null/undefined/0), use it
      if (this.wizardData.avgTransactionValue !== null && this.wizardData.avgTransactionValue !== undefined && this.wizardData.avgTransactionValue !== 0) {
        return this.wizardData.avgTransactionValue;
      }
      // Otherwise calculate from sales and transactions
      // Use numDailyTransactions directly - it's the permanent/base number
      // Don't calculate it from credit card transactions - the math goes the other way
      const totalDailyTx = this.wizardData.numDailyTransactions || 0;
      const annualTx = totalDailyTx * 365;
      if (!annualTx) return 0;
      return this.wizardData.annualCafeteriaSales / annualTx || 0;
    },
    numAnnualTransactions() {
      return this.numDailyTransactions * 365 || 0;
    },
    numAnnualChargeTransactions() {
      return this.numDailyChargeTransactions * 365 || 0;
    },
    // Calculated average transaction value from sales and transactions
    calculatedAvgTransactionValue() {
      if (this.numDailyTransactions === 0) return 0;
      return this.annualCafeteriaSales / this.totalAnnualTransactions || 0;
    },
    // Average daily sale - use user input if provided, otherwise calculated
    avgDailySale() {
      // If user provided a value, use it; otherwise use calculated
      if (this.userAvgTransactionValue !== null && this.userAvgTransactionValue !== undefined) {
        return this.userAvgTransactionValue;
      }
      return this.calculatedAvgTransactionValue;
    },
    avgDailySales() {
      return this.numDailyTransactions === 0 ? 0 : this.avgDailySale * this.numDailyTransactions || 0;
    },
    // Annual sales increase from traffic + average-check improvements.
    // - No cashless today: apply lift to ALL daily transactions (cash + card). Cash dining
    //   suppresses volume and ticket; going cashless typically lifts the full book of business
    //   (defaults: 20% traffic / 10% check ≈ ≥20% sales).
    // - Already cashless: apply lift only to in-house charge / payroll-deduction transactions
    //   (incremental growth on the cashless channel).
    avgDailyIncreaseSales() {
      let baseTx = 0;

      if (!this.hasInHouseCharge) {
        baseTx = this.numDailyTransactions || 0;
      } else {
        baseTx = this.numDailyChargeTransactions || 0;
        if (baseTx === 0 && this.participationCount > 0 && this.numDailyTransactions > 0) {
          baseTx = this.numDailyTransactions * (this.participationCount / 100);
        }
      }

      if (baseTx === 0 || !this.avgDailySale) return 0;

      const increasedTx = baseTx * (1 + (this.trafficIncreasePrecent / 100));
      const increasedAvgTicket = this.avgDailySale * (1 + (this.checkIncreasePrecent / 100));
      const newRevenue = increasedTx * increasedAvgTicket;
      const currentRevenue = baseTx * this.avgDailySale;

      return newRevenue - currentRevenue;
    },
    avgMonthlyIncreaseSales() {
      return this.avgAnnualIncreaseSales / 12 || 0;
    },
    avgAnnualIncreaseSales() {
      return this.avgDailyIncreaseSales * 365 || 0;
    },
    avgNetAnnualIncreaseSales() {
      let si = this.avgAnnualIncreaseSales || 0;
      let cogs = si * (this.foodCostPrecent / 100) || 0;
      let net = Currency(si).subtract(cogs).value || 0;
      if (!this.hasInHouseCharge) {
        net = Currency(net)
          .add(this.nonCashlessLaborSavings || 0)
          .add(this.nonCashlessCashHandlingSavings || 0).value;
      }
      return net;
    },
    totalAnnualTransactions() {
      return this.numDailyTransactions * 365 || 0;
    },
    totalAnnualChargeTransactions() {
      return this.numDailyChargeTransactions * 365 || 0;
    },
    increasedAvgCheck() {
      return this.avgDailySale * (this.checkIncreasePrecent / 100) || 0;
    },
    // Payback period: investment ÷ monthly net benefit from going / expanding cashless.
    // No existing system: use traffic/check sales lift (after COGS) + labor + cash-handling savings.
    // Existing system: credit retained ~20% baseline value of cashless + incremental traffic/check lift.
    paybackMonths() {
      if (this.investment === 0 || this.investment === null) return "N/A";

      let monthlyNet = 0;
      const statedIncrease = this.avgAnnualIncreaseSales || 0;
      const netStated = Currency(statedIncrease)
        .subtract(statedIncrease * (this.foodCostPrecent / 100)).value || 0;

      if (!this.hasInHouseCharge) {
        monthlyNet = (
          netStated
          + (this.nonCashlessLaborSavings || 0)
          + (this.nonCashlessCashHandlingSavings || 0)
        ) / 12;
      } else {
        const baselineValue = this.annualCafeteriaSales * 0.20;
        const netBaseline = Currency(baselineValue)
          .subtract(baselineValue * (this.foodCostPrecent / 100)).value || 0;
        monthlyNet = (netBaseline + netStated) / 12;
      }

      return monthlyNet === 0
        ? "N/A"
        : Currency(this.investment, { precision: 1, symbol: "" }).divide(monthlyNet).format();
    },
    hasInvestmentForPayback() {
      return this.investment !== null && this.investment !== undefined && Number(this.investment) > 0;
    },
    // User-facing payback label — never show bare "N/A months"
    paybackDisplay() {
      if (!this.hasInvestmentForPayback) {
        return 'Add 3-year investment to calculate';
      }
      if (this.paybackMonths === 'N/A') {
        return 'Unable to calculate from current inputs';
      }
      return `${this.paybackMonths} months`;
    },
    paybackNote() {
      if (!this.hasInvestmentForPayback) {
        return 'Payback needs a system investment amount (Inputs tab). $0 investment means we can’t estimate months to recover cost.';
      }
      return 'Time to recoup investment from projected net benefits';
    },
    newcareCtaMessage() {
      const salesLift = this.currency(this.avgAnnualIncreaseSales || 0).format();
      if (!this.hasInvestmentForPayback) {
        return `Based on your calculations, you could see ${salesLift} in annual sales increase. Enter a 3-year system investment on the Inputs tab to calculate payback—then see how Newcare's zero-fee cashless solution can help you achieve these results.`;
      }
      if (this.paybackMonths === 'N/A') {
        return `Based on your calculations, you could see ${salesLift} in annual sales increase. Payback couldn’t be estimated from the current benefit inputs—adjust sales and cost assumptions, or see how Newcare's zero-fee cashless solution can help.`;
      }
      return `Based on your calculations, you could see ${salesLift} in annual sales increase with a ${this.paybackMonths}-month payback period. See how Newcare's zero-fee cashless solution can help you achieve these results.`;
    },
    // Annual labor cost calculation
    // weeklyHours = hours of operation per week (e.g., 98 hours = 14 hrs/day × 7 days)
    // numFoodserviceStaff = number of staff per shift (on the clock at any one time)
    // averageWage = hourly wage per staff member
    // 
    // Calculation:
    // Staff-hours per week = numStaff × weeklyHours
    // Staff-hours per year = staffHoursPerWeek × 52
    // Annual cost = staffHoursPerYear × averageWage
    // 
    // Example: 1 staff, $1/hr, 1 hour/week = 1 × 1 × 52 × $1 = $52
    // Example: 2 staff, $1/hr, 1 hour/week = 2 × 1 × 52 × $1 = $104
    annualLaborCost() {
      const weeklyHours = this.weeklyHours || 98;
      const numStaff = this.numFoodserviceStaff || 0;
      const avgWage = this.averageWage || 0;
      
      if (numStaff === 0 || weeklyHours === 0) return 0;
      
      // Calculate total staff-hours per week (number of staff × hours of operation)
      const staffHoursPerWeek = numStaff * weeklyHours;
      
      // Calculate total staff-hours per year
      const staffHoursPerYear = staffHoursPerWeek * 52;
      
      // Annual cost = total staff-hours per year × average wage
      return staffHoursPerYear * avgWage;
    },
    // Gross sales (base + projected increase) for labor/prime cost analysis
    grossSales() {
      return (this.annualCafeteriaSales || 0) + (this.avgAnnualIncreaseSales || 0);
    },
    annualFoodCost() {
      return this.grossSales * (this.foodCostPrecent / 100) || 0;
    },
    laborCostPrecent() {
      return this.grossSales > 0 ? (this.annualLaborCost / this.grossSales) * 100 : 0;
    },
    primeCost() {
      return (this.annualLaborCost || 0) + (this.annualFoodCost || 0);
    },
    primeCostPrecent() {
      return this.grossSales > 0 ? (this.primeCost / this.grossSales) * 100 : 0;
    },
    // Sales per hour calculation
    salesPerHour() {
      const weeklyHours = this.weeklyHours || 98;
      const annualHours = weeklyHours * 52;
      return annualHours > 0 ? this.annualCafeteriaSales / annualHours : 0;
    },
    // Minimum / reference 20% sales lift when starting from no cashless (shown in UI notes;
    // actual modeled lift comes from traffic/check % on full transaction volume).
    nonCashlessSalesIncrease() {
      if (this.hasInHouseCharge) return 0;
      return this.annualCafeteriaSales * 0.20;
    },
    // Faster cashless checkout typically cuts cash-line labor ~20% when starting from cash-heavy ops.
    nonCashlessLaborSavings() {
      if (this.hasInHouseCharge) return 0;
      return this.annualLaborCost * 0.20;
    },
    // Cash volume remaining after charge + card activity (no cashless ⇒ charge ≈ 0).
    estimatedCashSalesAnnual() {
      if (this.hasInHouseCharge) return 0;
      const dailyCashTx = Math.max(
        0,
        (this.numDailyTransactions || 0)
          - (this.numDailyCreditCardTransactions || 0)
          - (this.numDailyChargeTransactions || 0)
      );
      return dailyCashTx * (this.avgDailySale || 0) * 365;
    },
    // Cash shrinkage (~1.5%) plus shift-closeout / reconciliation burden (~0.5%) on cash sales.
    nonCashlessCashHandlingSavings() {
      if (this.hasInHouseCharge) return 0;
      return this.estimatedCashSalesAnnual * 0.02;
    },
    // Labor after applying cashless labor reduction when applicable.
    effectiveAnnualLaborCost() {
      return Math.max(0, (this.annualLaborCost || 0) - (this.nonCashlessLaborSavings || 0));
    },
    // Convert credit card transactions to daily based on period
    numDailyCreditCardTransactions() {
      const transactions = this.creditCardTransactions || 0;
      if (transactions === 0) return 0;
      
      switch (this.creditCardTransactionsPeriod) {
        case 'daily':
          return transactions;
        case 'weekly':
          return transactions / 7;
        case 'monthly':
          return transactions / 30; // Approximate: 30 days per month
        default:
          return transactions;
      }
    },
    // Annual credit card fees calculation
    // Formula: (avg ticket × num transactions) × 2.9% + ($0.30 × num transactions)
    // Use the computed numDailyCreditCardTransactions (converted to daily)
    annualCreditCardFees() {
      const dailyCreditCardTransactions = this.numDailyCreditCardTransactions || 0;
      const annualCreditCardTransactions = dailyCreditCardTransactions * 365;
      if (annualCreditCardTransactions <= 0) return 0;
      
      const totalAnnualCreditCardSales = this.avgDailySale * annualCreditCardTransactions;
      const percentageFee = totalAnnualCreditCardSales * 0.029; // 2.9%
      const flatFee = annualCreditCardTransactions * 0.30; // $0.30 per transaction
      
      return Currency(percentageFee).add(flatFee).value || 0;
    },
    // Convert additional overhead to annual amount based on period
    annualAdditionalOverhead() {
      const overhead = this.additionalOverhead || 0;
      if (overhead === 0) return 0;
      
      switch (this.additionalOverheadPeriod) {
        case 'daily':
          return overhead * 365;
        case 'monthly':
          return overhead * 12;
        case 'annual':
        default:
          return overhead;
      }
    },
    // Annual system investment cost (3-year investment / 3)
    annualSystemInvestment() {
      return (this.investment || 0) / 3;
    },
    // Net profit after all costs
    netProfit() {
      const baseSales = this.annualCafeteriaSales || 0;
      const salesIncrease = this.avgAnnualIncreaseSales || 0;
      const totalSales = baseSales + salesIncrease;

      const foodCost = totalSales * (this.foodCostPrecent / 100) || 0;
      const labor = this.effectiveAnnualLaborCost || 0;
      const creditCardFees = this.annualCreditCardFees || 0;
      const additionalOverhead = this.annualAdditionalOverhead || 0;
      const systemInvestment = this.annualSystemInvestment || 0;
      const cashHandlingSavings = this.nonCashlessCashHandlingSavings || 0;

      return Currency(totalSales)
        .subtract(foodCost)
        .subtract(labor)
        .subtract(creditCardFees)
        .subtract(additionalOverhead)
        .subtract(systemInvestment)
        .add(cashHandlingSavings)
        .value || 0;
    },
    // Cost breakdown pie chart data - shows where revenue goes
    // All components should add up to total annual sales
    costBreakdownChartData() {
      const sales = this.annualCafeteriaSales || 0;
      const labor = this.effectiveAnnualLaborCost || 0;
      const creditCardFees = this.annualCreditCardFees || 0;
      const foodCost = sales * (this.foodCostPrecent / 100) || 0;
      const additionalOverhead = this.annualAdditionalOverhead || 0;
      const systemInvestment = this.annualSystemInvestment || 0;
      
      // Calculate net profit (what's left after all costs)
      const netProfit = Currency(sales)
        .subtract(foodCost)
        .subtract(labor)
        .subtract(creditCardFees)
        .subtract(additionalOverhead)
        .subtract(systemInvestment)
        .value || 0;
      
      // Build labels and data arrays conditionally
      const labels = ['Food Cost', 'Labor Cost', 'Credit Card Fees'];
      const data = [foodCost, labor, creditCardFees];
      
      // Only include system investment if it's greater than 0
      if (systemInvestment > 0) {
        labels.push('System Investment');
        data.push(systemInvestment);
      }
      
      // Only include additional overhead if it's greater than 0
      if (additionalOverhead > 0) {
        labels.push('Additional Overhead');
        data.push(additionalOverhead);
      }
      
      labels.push('Net Profit');
      data.push(Math.max(0, netProfit));
      
      // Build background colors array
      const backgroundColor = [
        'rgba(116, 168, 146, 0.8)', // Green for food cost
        'rgba(199, 82, 42, 0.8)',   // Orange for labor
        'rgba(251, 242, 196, 0.8)', // Yellow for fees
      ];
      const borderColor = [
        '#74a892',
        '#c7522a',
        '#fbf2c4',
      ];
      
      if (systemInvestment > 0) {
        backgroundColor.push('rgba(200, 100, 200, 0.8)'); // Purple for system investment
        borderColor.push('#c864c8');
      }
      
      if (additionalOverhead > 0) {
        backgroundColor.push('rgba(128, 128, 128, 0.8)'); // Gray for additional overhead
        borderColor.push('#808080');
      }
      
      backgroundColor.push('rgba(74, 168, 146, 0.8)'); // Teal for net profit
      borderColor.push('#4aa892');
      
      return {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          borderWidth: 2
        }]
      };
    },
    pieChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 1.2,
        legend: {
          position: 'bottom',
          labels: {
            fontColor: '#fbf2c4',
            fontSize: 11,
            padding: 10,
            boxWidth: 12
          }
        },
        tooltips: {
          backgroundColor: '#2a344a',
          titleFontColor: '#fbf2c4',
          bodyFontColor: '#fbf2c4',
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.labels[tooltipItem.index];
              const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
              return `${label}: ${Currency(value).format()}`;
            }
          }
        }
      };
    },
    salesChartData() {
      return {
        labels: ["Daily", "Monthly", "Annual"],
        datasets: [
          {
            label: "Sales Increase ($)",
            data: [
              this.avgDailyIncreaseSales,
              this.avgMonthlyIncreaseSales,
              this.avgAnnualIncreaseSales,
            ],
            backgroundColor: "rgba(199, 82, 42, 0.2)",
            borderColor: "#c7522a",
            borderWidth: 2,
            tension: 0.4,
          },
        ],
      };
    },
    roiChartData() {
      return {
        labels: ["Investment", "Net Annual Increase"],
        datasets: [
          {
            label: "ROI Breakdown ($)",
            data: [this.investment, this.avgNetAnnualIncreaseSales],
            backgroundColor: ["#c7522a", "#74a892"],
            borderColor: ["#c7522a", "#74a892"],
            borderWidth: 1,
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: { beginAtZero: true, fontColor: "#fbf2c4" },
            gridLines: { color: "rgba(251, 242, 196, 0.1)" },
          }],
          xAxes: [{
            ticks: { fontColor: "#fbf2c4" },
            gridLines: { color: "rgba(251, 242, 196, 0.1)" },
          }],
        },
        legend: {
          labels: { fontColor: "#fbf2c4" },
        },
        tooltips: {
          backgroundColor: "#2a344a",
        },
      };
    },
    // 5-Year Financial Forecast
    fiveYearForecast() {
      const baseSales = this.annualCafeteriaSales || 0;
      const annualIncrease = this.avgAnnualIncreaseSales || 0;
      const baseNetProfit = this.netProfit || 0;
      const investment = this.investment || 0;
      const annualInvestment = this.annualSystemInvestment || 0;
      
      const forecast = [];
      let cumulativeNet = -investment; // Start with negative investment
      
      for (let year = 1; year <= 5; year++) {
        const totalSales = baseSales + annualIncrease;
        const netProfit = baseNetProfit;
        cumulativeNet += netProfit;
        
        // Only subtract investment in years 1-3
        if (year <= 3) {
          cumulativeNet -= annualInvestment;
        }
        
        const roi = investment > 0 ? ((cumulativeNet / investment) * 100) : 0;
        
        forecast.push({
          year: `Year ${year}`,
          totalSales,
          netProfit,
          cumulativeNet,
          roi
        });
      }
      
      return forecast;
    },
    fiveYearNetBenefit() {
      const forecast = this.fiveYearForecast;
      return forecast.length > 0 ? forecast[forecast.length - 1].cumulativeNet : 0;
    },
    threeYearROI() {
      const forecast = this.fiveYearForecast;
      return forecast.length >= 3 ? forecast[2].roi : 0;
    },
    fiveYearROI() {
      const forecast = this.fiveYearForecast;
      return forecast.length > 0 ? forecast[forecast.length - 1].roi : 0;
    },
    fiveYearForecastData() {
      const forecast = this.fiveYearForecast;
      return {
        labels: forecast.map(f => f.year),
        datasets: [{
          label: 'Total Sales',
          data: forecast.map(f => f.totalSales),
          borderColor: '#c7522a',
          backgroundColor: 'rgba(199, 82, 42, 0.2)',
          borderWidth: 2,
          yAxisID: 'y'
        }, {
          label: 'Net Profit',
          data: forecast.map(f => f.netProfit),
          borderColor: '#4aa892',
          backgroundColor: 'rgba(74, 168, 146, 0.2)',
          borderWidth: 2,
          yAxisID: 'y'
        }, {
          label: 'Cumulative Net',
          data: forecast.map(f => f.cumulativeNet),
          borderColor: '#fbf2c4',
          backgroundColor: 'rgba(251, 242, 196, 0.2)',
          borderWidth: 2,
          yAxisID: 'y1',
          borderDash: [5, 5]
        }]
      };
    },
    forecastChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            id: 'y',
            ticks: { beginAtZero: true, fontColor: '#fbf2c4' },
            gridLines: { color: 'rgba(251, 242, 196, 0.1)' }
          }, {
            id: 'y1',
            position: 'right',
            ticks: { fontColor: '#fbf2c4' },
            gridLines: { display: false }
          }],
          xAxes: [{
            ticks: { fontColor: '#fbf2c4' },
            gridLines: { color: 'rgba(251, 242, 196, 0.1)' }
          }]
        },
        legend: {
          labels: { fontColor: '#fbf2c4' }
        },
        tooltips: {
          backgroundColor: '#2a344a',
          titleFontColor: '#fbf2c4',
          bodyFontColor: '#fbf2c4'
        }
      };
    },
    // Sensitivity Analysis Scenarios
    sensitivityScenarios() {
      const baseSales = this.annualCafeteriaSales || 0;
      const currentIncrease = this.avgAnnualIncreaseSales || 0;
      const dailyChargeTx = this.numDailyChargeTransactions || (this.numDailyTransactions * (this.participationCount / 100)) || 0;
      const currentTicket = this.avgDailySale || 0;
      const currentParticipation = this.participationCount || 0;
      const trafficIncrease = this.trafficIncreasePrecent || 0;
      const checkIncrease = this.checkIncreasePrecent || 0;
      const foodCostPct = this.foodCostPrecent / 100 || 0;
      
      // Scenario 1: +5% Average Ticket
      // Applies to ALL transactions—a "ninja" boost. Revenue +5%; after COGS, net profit impact.
      // Labor, credit card fees, etc. stay constant.
      const totalAnnualTx = (this.numDailyTransactions || 0) * 365;
      const ticket5RevenueIncrease = totalAnnualTx * currentTicket * 0.05;
      const ticket5Annual = ticket5RevenueIncrease * (1 - foodCostPct);
      
      // Scenario 2: +10% Participation (revenue delta on cashless; convert to net after COGS)
      const newParticipation = Math.min(100, currentParticipation + 10);
      const newChargeTx = this.numDailyTransactions * (newParticipation / 100);
      const participation10RevenueIncrease = newChargeTx * (1 + (trafficIncrease / 100)) * (currentTicket * (1 + (checkIncrease / 100))) - (dailyChargeTx * currentTicket);
      const participation10Annual = participation10RevenueIncrease * 365 * (1 - foodCostPct);
      
      // Scenario 3: Combined (+5% Ticket + 10% Participation)
      // Independent effects: ticket boost on all tx + participation increase benefit
      const combinedAnnual = ticket5Annual + participation10Annual;
      
      return {
        ticket5: {
          annual: ticket5Annual,
          fiveYear: ticket5Annual * 5
        },
        participation10: {
          annual: participation10Annual,
          fiveYear: participation10Annual * 5
        },
        combined: {
          annual: combinedAnnual,
          fiveYear: combinedAnnual * 5
        }
      };
    },
    sensitivityAnalysisData() {
      const scenarios = this.sensitivityScenarios;
      return {
        labels: ['Current', '+5% Ticket', '+10% Participation', 'Combined'],
        datasets: [{
          label: 'Annual Net Profit Impact',
          data: [
            this.avgNetAnnualIncreaseSales,
            scenarios.ticket5.annual,
            scenarios.participation10.annual,
            scenarios.combined.annual
          ],
          backgroundColor: [
            'rgba(199, 82, 42, 0.6)',
            'rgba(74, 168, 146, 0.6)',
            'rgba(251, 242, 196, 0.6)',
            'rgba(200, 100, 200, 0.6)'
          ],
          borderColor: [
            '#c7522a',
            '#4aa892',
            '#fbf2c4',
            '#c864c8'
          ],
          borderWidth: 2
        }]
      };
    },
    sensitivityChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              fontColor: '#fbf2c4',
              callback: (value) => Currency(value).format()
            },
            gridLines: { color: 'rgba(251, 242, 196, 0.1)' }
          }],
          xAxes: [{
            ticks: { fontColor: '#fbf2c4' },
            gridLines: { color: 'rgba(251, 242, 196, 0.1)' }
          }]
        },
        legend: {
          display: false
        },
        tooltips: {
          backgroundColor: '#2a344a',
          titleFontColor: '#fbf2c4',
          bodyFontColor: '#fbf2c4',
          callbacks: {
            label: (tooltipItem) => Currency(tooltipItem.yLabel).format()
          }
        }
      };
    },
    cumulativeCashFlowData() {
      const forecast = this.fiveYearForecast;
      return {
        labels: ['Year 0', ...forecast.map(f => f.year)],
        datasets: [{
          label: 'Cumulative Cash Flow',
          data: [-(this.investment || 0), ...forecast.map(f => f.cumulativeNet)],
          borderColor: '#4aa892',
          backgroundColor: 'rgba(74, 168, 146, 0.2)',
          borderWidth: 2,
          fill: true
        }]
      };
    },
    cashFlowChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            ticks: {
              fontColor: '#fbf2c4',
              callback: (value) => Currency(value).format()
            },
            gridLines: {
              color: 'rgba(251, 242, 196, 0.1)',
              zeroLineColor: '#fbf2c4',
              zeroLineWidth: 2
            }
          }],
          xAxes: [{
            ticks: { fontColor: '#fbf2c4' },
            gridLines: { color: 'rgba(251, 242, 196, 0.1)' }
          }]
        },
        legend: {
          labels: { fontColor: '#fbf2c4' }
        },
        tooltips: {
          backgroundColor: '#2a344a',
          titleFontColor: '#fbf2c4',
          bodyFontColor: '#fbf2c4',
          callbacks: {
            label: (tooltipItem) => Currency(tooltipItem.yLabel).format()
          }
        }
      };
    },
  },
  methods: {
    currency(x, y) {
      return Currency(x, y);
    },
    // Wizard-specific helpers to keep daily transactions, charge transactions,
    // and participation percentage in sync while the user is in the setup flow.
    wizardDailyTransactionsChanged() {
      const dailyTx = this.wizardData.numDailyTransactions || 0;
      if (dailyTx) {
        if (this.wizardData.participationCount) {
          // Recompute charge transactions from participation %
          this.wizardData.numDailyChargeTransactions = Currency(dailyTx)
            .multiply(this.wizardData.participationCount)
            .divide(100).value || 0;
        } else if (this.wizardData.numDailyChargeTransactions) {
          // Infer participation % from charge vs total transactions
          this.wizardData.participationCount = Currency(this.wizardData.numDailyChargeTransactions)
            .divide(dailyTx)
            .multiply(100).value || 0;
        }
        
        // Default credit card transactions to 50% of total transactions, adjusted for period
        this.updateWizardCreditCardDefault();
      }
    },
    wizardCreditCardPeriodChanged() {
      // When period changes, update the default credit card transactions
      this.updateWizardCreditCardDefault();
    },
    updateWizardCreditCardDefault() {
      // Only update if credit card transactions is null/undefined/0/empty (not manually set)
      if (this.wizardData.creditCardTransactions === null || 
          this.wizardData.creditCardTransactions === undefined || 
          this.wizardData.creditCardTransactions === 0 ||
          this.wizardData.creditCardTransactions === '') {
        const dailyTx = this.wizardData.numDailyTransactions || 0;
        const period = this.wizardData.creditCardTransactionsPeriod || 'weekly';
        
        if (dailyTx > 0) {
          // Calculate 50% of daily transactions, then convert to selected period
          const halfDailyTx = dailyTx * 0.5;
          
          switch (period) {
            case 'daily':
              this.wizardData.creditCardTransactions = Math.round(halfDailyTx);
              break;
            case 'weekly':
              this.wizardData.creditCardTransactions = Math.round(halfDailyTx * 7);
              break;
            case 'monthly':
              this.wizardData.creditCardTransactions = Math.round(halfDailyTx * 30); // Approximate
              break;
          }
        }
      }
    },
    wizardDailyChargeTransactionsChanged() {
      const dailyTx = this.wizardData.numDailyTransactions || 0;
      if (dailyTx) {
        this.wizardData.participationCount = Currency(this.wizardData.numDailyChargeTransactions || 0)
          .divide(dailyTx)
          .multiply(100).value || 0;
      }
    },
    wizardParticipationCountChanged() {
      const dailyTx = this.wizardData.numDailyTransactions || 0;
      if (dailyTx) {
        this.wizardData.numDailyChargeTransactions = Currency(dailyTx)
          .multiply(this.wizardData.participationCount || 0)
          .divide(100).value || 0;
      }
    },
    wizardHasInHouseChargeChanged() {
      // Update defaults when checkbox changes in wizard
      // If they DON'T have cashless: default to 20% traffic, 10% check
      // If they DO have cashless: default to 5% traffic, 2% check
      if (this.wizardData.hasInHouseCharge) {
        // Have cashless - only set defaults if both are blank/zero
        if (!this.wizardData.trafficIncreasePrecent && !this.wizardData.checkIncreasePrecent) {
          this.wizardData.trafficIncreasePrecent = 5;
          this.wizardData.checkIncreasePrecent = 2;
        }
      } else {
        // Don't have cashless - only set defaults if both are blank/zero
        if (!this.wizardData.trafficIncreasePrecent && !this.wizardData.checkIncreasePrecent) {
          this.wizardData.trafficIncreasePrecent = 20;
          this.wizardData.checkIncreasePrecent = 10;
        }
      }
    },
    // When total daily transactions change, keep the relationship between
    // total transactions, charge transactions, and participation consistent.
    dailyTransactionsChanged() {
      if (this.numDailyTransactions) {
        if (this.participationCount) {
          // If a participation percentage is set, recompute charge transactions from it
          this.numDailyChargeTransactions = Currency(this.numDailyTransactions)
            .multiply(this.participationCount)
            .divide(100).value || 0;
        } else if (this.numDailyChargeTransactions) {
          // Otherwise, infer participation from total vs charge transactions
          this.participationCount = Currency(this.numDailyChargeTransactions)
            .divide(this.numDailyTransactions)
            .multiply(100).value || 0;
        }
      }
      this.updateCharts();
    },
    // When the number of charge (payroll deduction / house account) transactions changes,
    // derive participation as the percentage of total daily transactions.
    dailyChargeTransactionsChanged() {
      if (this.numDailyTransactions) {
        this.participationCount = Currency(this.numDailyChargeTransactions)
          .divide(this.numDailyTransactions)
          .multiply(100).value || 0;
        
        // When charge transactions change, reduce credit card transactions
        // Credit card transactions = total daily transactions - charge transactions
        const newCreditCardTxDaily = Math.max(0, this.numDailyTransactions - this.numDailyChargeTransactions);
        
        // Convert to the selected period
        const period = this.creditCardTransactionsPeriod || 'weekly';
        switch (period) {
          case 'daily':
            this.creditCardTransactions = Math.round(newCreditCardTxDaily);
            break;
          case 'weekly':
            this.creditCardTransactions = Math.round(newCreditCardTxDaily * 7);
            break;
          case 'monthly':
            this.creditCardTransactions = Math.round(newCreditCardTxDaily * 30);
            break;
        }
      }
      this.updateCharts();
    },
    // When participation percentage is edited directly, recompute charge transactions
    // from the total daily transaction count.
    participationCountChanged() {
      if (this.numDailyTransactions) {
        this.numDailyChargeTransactions = Currency(this.numDailyTransactions)
          .multiply(this.participationCount)
          .divide(100).value || 0;
      }
      this.updateCharts();
    },
        updateCharts() {
      this.$nextTick(() => {
        if (this.$refs.lineChart) {
          this.$refs.lineChart.renderChart(this.salesChartData, this.chartOptions);
        }
        if (this.$refs.barChart) {
          this.$refs.barChart.renderChart(this.roiChartData, this.chartOptions);
        }
        if (this.$refs.costPieChart) {
          this.$refs.costPieChart.renderChart(this.costBreakdownChartData, this.pieChartOptions);
        }
        // Update new analysis charts
        this.renderAnalysisCharts();
      });
    },
    getPdfChartOptions(baseOptions, type) {
      const pdfOverrides = {
        scales: {
          yAxes: baseOptions.scales?.yAxes?.map((ax, i) => ({
            ...ax,
            ticks: { ...ax.ticks, fontColor: '#2c3e50' },
            gridLines: { ...ax.gridLines, color: 'rgba(0,0,0,0.12)' }
          })) || [],
          xAxes: baseOptions.scales?.xAxes?.map((ax) => ({
            ...ax,
            ticks: { ...ax.ticks, fontColor: '#2c3e50' },
            gridLines: { ...ax.gridLines, color: 'rgba(0,0,0,0.12)' }
          })) || []
        },
        legend: baseOptions.legend ? { ...baseOptions.legend, labels: { ...baseOptions.legend.labels, fontColor: '#2c3e50' } } : undefined
      };
      return { ...baseOptions, ...pdfOverrides };
    },
    renderAnalysisCharts(pdfMode = false) {
      this.$nextTick(() => {
        const pdfOpt = (opts) => pdfMode ? this.getPdfChartOptions(opts) : opts;
        if (this.$refs.fiveYearForecastChart) {
          const data = this.fiveYearForecastData;
          const options = pdfOpt(this.forecastChartOptions);
          if (data && data.labels && data.labels.length > 0 && data.datasets && data.datasets.length > 0) {
            try {
              if (this.$refs.fiveYearForecastChart.$el && this.$refs.fiveYearForecastChart.$el.offsetParent !== null) {
                this.$refs.fiveYearForecastChart.renderChart(data, options);
              }
            } catch (e) {}
          }
        }
        if (this.$refs.sensitivityChart) {
          const data = this.sensitivityAnalysisData;
          const options = pdfOpt(this.sensitivityChartOptions);
          if (data && data.labels && data.labels.length > 0 && data.datasets && data.datasets.length > 0) {
            try {
              if (this.$refs.sensitivityChart.$el && this.$refs.sensitivityChart.$el.offsetParent !== null) {
                this.$refs.sensitivityChart.renderChart(data, options);
              }
            } catch (e) {}
          }
        }
        if (this.$refs.cashFlowChart) {
          const data = this.cumulativeCashFlowData;
          const options = pdfOpt(this.cashFlowChartOptions);
          if (data && data.labels && data.labels.length > 0 && data.datasets && data.datasets.length > 0) {
            try {
              if (this.$refs.cashFlowChart.$el && this.$refs.cashFlowChart.$el.offsetParent !== null) {
                this.$refs.cashFlowChart.renderChart(data, options);
              }
            } catch (e) {}
            }
        }
      });
    },
    onTabChanged(tabIndex) {
      // When Analysis tab (index 1) is activated, render the charts
      if (tabIndex === 1) {
        // Wait for DOM to be ready, then render charts
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderAnalysisCharts();
          }, 300);
        });
      }
    },
    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    async handlePdfDownload() {
      if (!this.pdfUserData.name || !this.pdfUserData.email || !this.isValidEmail(this.pdfUserData.email)) {
        return;
      }
      
      this.isGeneratingPdf = true;
      
      try {
        // First, send email capture to Netlify Function
        await this.sendEmailCapture();
        
        // Then generate and download PDF
        await this.generatePdf();
        
        // Close modal and reset form
        this.showEmailModal = false;
        this.pdfUserData = { company: '', name: '', email: '' };
        
        this.$bvToast.toast('PDF downloaded successfully! Check your email for a copy.', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 5000
        });
      } catch (error) {
        this.$bvToast.toast('There was an error generating your PDF. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.isGeneratingPdf = false;
      }
    },
    async sendEmailCapture() {
      try {
        const formData = new URLSearchParams();
        formData.append('form-name', 'pdf-download');
        formData.append('name', (this.pdfUserData.name || '').trim());
        formData.append('email', (this.pdfUserData.email || '').trim());
        formData.append('company', (this.pdfUserData.company || '').trim());
        formData.append('calculator', 'healthcare');
        formData.append('calculatorData', JSON.stringify(this.getCalculatorSummary()));
        formData.append('bot-field', this.pdfUserData.numEmployeesHoneypot || '');

        await fetch('/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formData.toString()
        });
      } catch (error) {
        // Don't throw - allow PDF download even if email capture fails
      }
    },
    getCalculatorSummary() {
      return {
        numEmployees: this.numEmployees,
        annualCafeteriaSales: this.annualCafeteriaSales,
        numDailyTransactions: this.numDailyTransactions,
        creditCardTransactions: this.creditCardTransactions,
        creditCardTransactionsPeriod: this.creditCardTransactionsPeriod,
        avgTransactionValue: this.avgDailySale,
        hasInHouseCharge: this.hasInHouseCharge,
        numDailyChargeTransactions: this.numDailyChargeTransactions,
        participationCount: this.participationCount,
        investment: this.investment,
        trafficIncreasePrecent: this.trafficIncreasePrecent,
        checkIncreasePrecent: this.checkIncreasePrecent,
        weeklyHours: this.weeklyHours,
        numFoodserviceStaff: this.numFoodserviceStaff,
        averageWage: this.averageWage,
        foodCostPrecent: this.foodCostPrecent,
        additionalOverhead: this.additionalOverhead,
        additionalOverheadPeriod: this.additionalOverheadPeriod
      };
    },
    getChartBase64(refName) {
      try {
        const chartRef = this.$refs[refName];
        if (!chartRef || !chartRef.$data || !chartRef.$data._chart) return null;
        const chart = chartRef.$data._chart;
        if (!chart) return null;
        if (chart.canvas && chart.canvas.parentNode) {
          chart.resize();
        }
        if (typeof chart.toBase64Image === 'function') {
          return chart.toBase64Image('image/png');
        }
        const canvas = chartRef.$el && chartRef.$el.querySelector ? chartRef.$el.querySelector('canvas') : (chartRef.$el && chartRef.$el.tagName === 'CANVAS' ? chartRef.$el : null);
        if (canvas) return canvas.toDataURL('image/png');
        return null;
      } catch (e) {
        return null;
      }
    },
    async generatePdf() {
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      // Executive-grade palette: Deep Navy, Charcoal, Brushed Gold
      const primaryColor = [26, 54, 93];   // Deep Navy #1a365d
      const accentColor = [201, 162, 39];  // Brushed Gold #c9a227
      const charcoalColor = [45, 55, 72];  // Charcoal #2d3748
      const darkColor = [40, 40, 40];
      const grayColor = [90, 90, 90];
      const lightGray = [245, 245, 245];

      // Render charts with PDF-friendly contrast (dark text on light) before capture
      this.renderAnalysisCharts(true);
      if (this.$refs.costPieChart) {
        const pieOpts = this.getPdfChartOptions(this.pieChartOptions);
        this.$refs.costPieChart.renderChart(this.costBreakdownChartData, pieOpts);
      }
      await this.$nextTick();
      await new Promise(r => setTimeout(r, 600));
      const chartImgWidth = contentWidth;
      const chartImgHeight = 75; // Taller for line/bar charts
      const chartPieSize = 72; // Square for pie chart
      const chartImages = {
        fiveYearForecast: this.getChartBase64('fiveYearForecastChart'),
        sensitivity: this.getChartBase64('sensitivityChart'),
        cashFlow: this.getChartBase64('cashFlowChart'),
        costPie: this.getChartBase64('costPieChart')
      };

      const newPage = () => {
        doc.addPage();
        return 20;
      };
      const checkPage = (y, space = 15) => {
        if (y > pageHeight - 30) return newPage();
        return y;
      };
      const sectionTitle = (title, y) => {
        y = checkPage(y, 25);
        doc.setFillColor(...primaryColor);
        doc.rect(0, y - 5, pageWidth, 14, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(title, margin, y + 4);
        doc.setTextColor(...darkColor);
        doc.setFont(undefined, 'normal');
        return y + 18;
      };
      const addChartImage = (imgData, y, caption, isPie = false, customHeight = null) => {
        if (!imgData || imgData.length < 100) return y;
        const w = isPie ? chartPieSize : chartImgWidth;
        const h = isPie ? chartPieSize : (customHeight || chartImgHeight);
        y = checkPage(y, h + 15);
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.3);
        const xOffset = isPie ? margin + (contentWidth - chartPieSize) / 2 : margin;
        doc.rect(xOffset, y, w, h, 'FD');
        try {
          doc.addImage(imgData, 'PNG', xOffset + 2, y + 2, w - 4, h - 4);
        } catch (e) {
          // Skip if image fails
        }
        y += h + 5;
        if (caption) {
          doc.setFontSize(8);
          doc.setTextColor(...grayColor);
          doc.text(caption, isPie ? pageWidth / 2 : margin, y, { align: isPie ? 'center' : 'left' });
          y += 8;
        }
        return y;
      };

      // === COVER PAGE (C-Suite Styled) ===
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 55, 'F');
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.5);
      doc.line(0, 55, pageWidth, 55);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(26);
      doc.setFont(undefined, 'bold');
      doc.text('Newcare International', pageWidth / 2, 28, { align: 'center' });
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(255, 248, 220); // Brushed gold tint on navy
      doc.text('Cashless Dining ROI Analysis', pageWidth / 2, 42, { align: 'center' });
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.text('Strategic Financial Analysis for Healthcare & Senior Living', pageWidth / 2, 50, { align: 'center' });

      let yPos = 80;
      doc.setTextColor(...darkColor);
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      const companyName = (this.pdfUserData.company || '').trim();
      const userName = (this.pdfUserData.name || '').trim();
      if (companyName) {
        doc.setFontSize(10);
        doc.setTextColor(...grayColor);
        doc.text('Prepared for', pageWidth / 2, yPos, { align: 'center' });
        yPos += 8;
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...darkColor);
        doc.text(companyName, pageWidth / 2, yPos, { align: 'center' });
        yPos += 10;
      }
      if (userName) {
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        doc.text(userName, pageWidth / 2, yPos, { align: 'center' });
        yPos += 15;
      }
      yPos += 10;

      doc.setFontSize(24);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...primaryColor);
      doc.text('ROI Analysis Report', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.8);
      doc.line(pageWidth / 2 - 40, yPos, pageWidth / 2 + 40, yPos);
      yPos += 12;
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...grayColor);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 22;

      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.4);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 12;
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...primaryColor);
      doc.text('www.newcare.io', pageWidth / 2, yPos, { align: 'center' });
      yPos += 7;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...grayColor);
      doc.text('cashlessFyi@newcare.io', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      doc.setFontSize(9);
      doc.setTextColor(...grayColor);
      doc.text('Find this calculator here: https://www.cashless.fyi', pageWidth / 2, yPos, { align: 'center' });
      yPos += 14;
      doc.setFillColor(...lightGray);
      doc.rect(margin, yPos - 3, contentWidth, 14, 'F');
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.rect(margin, yPos - 3, contentWidth, 14, 'S');
      doc.setFontSize(8);
      doc.setTextColor(...grayColor);
      doc.text('Net profit figures are based on the information provided. Your actual results will vary. Use the Additional Overhead field to estimate other expenses for more accurate projections.', margin + 3, yPos + 5, { maxWidth: contentWidth - 6 });

      // === PAGE 2: EXECUTIVE SUMMARY + KEY RESULTS (Combined, 1 page) ===
      yPos = newPage();
      yPos = sectionTitle('Executive Summary', yPos);

      // Hero callout: Net Profit Margin (the "win" stat)
      const netProfitMarginPct = this.grossSales > 0 ? ((this.netProfit / this.grossSales) * 100) : 0;
      const calloutH = 22;
      yPos = checkPage(yPos, calloutH);
      doc.setFillColor(...primaryColor);
      doc.setDrawColor(...accentColor);
      doc.setLineWidth(0.5);
      doc.rect(margin, yPos, contentWidth, calloutH, 'FD');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text('Net Profit Margin', margin + 8, yPos + 8);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...accentColor);
      doc.text(netProfitMarginPct > 0 ? `${netProfitMarginPct.toFixed(2)}%` : 'N/A', margin + contentWidth - 8, yPos + 14, { align: 'right' });
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(255, 255, 255);
      doc.text('Your strongest metric — healthy margin indicates efficient operations', margin + 8, yPos + 18);
      yPos += calloutH + 10;

      // Key metrics table (condensed)
      const summaryData = [
        ['Investment (3-year)', this.currency(this.investment).format()],
        ['Payback Period', this.paybackDisplay],
        ['5-Year Net Profit', this.currency(this.fiveYearNetBenefit).format()],
        ['Current Net Profit', this.currency(this.netProfit).format()],
        ['Annual Sales Increase', this.currency(this.avgAnnualIncreaseSales).format()],
        ['Credit Card Fee Savings', this.currency(this.annualCreditCardFees).format()],
        ['Labor Cost per Transaction', this.currency(this.annualLaborCost / this.totalAnnualTransactions || 0).format()]
      ];
      const summaryCol1 = 95;
      const summaryRowH = 6;
      const summaryTableTop = yPos;
      doc.setFontSize(9);
      doc.setFillColor(...charcoalColor);
      doc.rect(margin, summaryTableTop, contentWidth, summaryRowH, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      doc.text('Metric', margin + summaryCol1 - 4, summaryTableTop + 4, { align: 'right' });
      doc.text('Value', margin + summaryCol1 + 4, summaryTableTop + 4);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...darkColor);
      yPos = summaryTableTop + summaryRowH;
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(margin + summaryCol1, summaryTableTop, margin + summaryCol1, summaryTableTop + summaryRowH);
      summaryData.forEach(([label, value], i) => {
        yPos = checkPage(yPos, summaryRowH);
        doc.setFillColor(i % 2 === 0 ? 255 : 248, i % 2 === 0 ? 255 : 248, i % 2 === 0 ? 255 : 248);
        doc.rect(margin, yPos, contentWidth, summaryRowH, 'FD');
        doc.line(margin + summaryCol1, yPos, margin + summaryCol1, yPos + summaryRowH);
        doc.setTextColor(...grayColor);
        doc.text(label, margin + summaryCol1 - 2, yPos + 4, { align: 'right' });
        doc.setTextColor(...darkColor);
        doc.setFont(undefined, 'bold');
        doc.text(String(value), margin + summaryCol1 + 2, yPos + 4);
        doc.setFont(undefined, 'normal');
        yPos += summaryRowH;
      });
      yPos += 8;
      doc.setFontSize(8);
      doc.setTextColor(...grayColor);
      doc.text('Net profit figures are based on the information provided. Your actual results will vary.', margin, yPos, { maxWidth: contentWidth });
      yPos += 12;

      // Cost Breakdown Pie (compact)
      yPos = addChartImage(chartImages.costPie, yPos, 'Cost breakdown by category', true);

      // === PAGE 3: SENSITIVITY ANALYSIS (HERO - largest chart) ===
      yPos = newPage();
      yPos = sectionTitle('Sensitivity Analysis: The Power of Small Changes', yPos);
      doc.setFontSize(9);
      doc.setTextColor(...grayColor);
      doc.text('Small operational improvements yield significant financial impact. This is your upside opportunity.', margin, yPos, { maxWidth: contentWidth });
      yPos += 10;
      const heroChartHeight = 95;
      yPos = addChartImage(chartImages.sensitivity, yPos, null, false, heroChartHeight);
      const sensColWidths = [90, 55, 55];
      const sensRowH = 7;
      const sensTableTop = yPos;
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(...primaryColor);
      doc.setTextColor(255, 255, 255);
      doc.rect(margin, sensTableTop, contentWidth, sensRowH, 'F');
      doc.text('Scenario', margin + 2, sensTableTop + 4);
      doc.text('Annual Impact', margin + sensColWidths[0] + 2, sensTableTop + 4);
      doc.text('5-Year Impact', margin + sensColWidths[0] + sensColWidths[1] + 2, sensTableTop + 4);
      yPos = sensTableTop + sensRowH;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...darkColor);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      const sensRows = [
        ['Current Projection', this.currency(this.avgNetAnnualIncreaseSales).format(), this.currency(this.avgNetAnnualIncreaseSales * 5).format()],
        ['+5% Avg Ticket', this.currency(this.sensitivityScenarios.ticket5.annual).format(), this.currency(this.sensitivityScenarios.ticket5.fiveYear).format()],
        ['+10% Participation', this.currency(this.sensitivityScenarios.participation10.annual).format(), this.currency(this.sensitivityScenarios.participation10.fiveYear).format()],
        ['+5% Ticket + 10% Part.', this.currency(this.sensitivityScenarios.combined.annual).format(), this.currency(this.sensitivityScenarios.combined.fiveYear).format()]
      ];
      sensRows.forEach(([scenario, annual, fiveYr], rowIdx) => {
        yPos = checkPage(yPos, sensRowH);
        doc.setFillColor(rowIdx % 2 === 0 ? 255 : 248, rowIdx % 2 === 0 ? 255 : 248, rowIdx % 2 === 0 ? 255 : 248);
        doc.rect(margin, yPos, contentWidth, sensRowH, 'FD');
        doc.line(margin + sensColWidths[0], yPos, margin + sensColWidths[0], yPos + sensRowH);
        doc.line(margin + sensColWidths[0] + sensColWidths[1], yPos, margin + sensColWidths[0] + sensColWidths[1], yPos + sensRowH);
        doc.text(scenario, margin + 2, yPos + 4);
        doc.text(annual, margin + sensColWidths[0] + 2, yPos + 4);
        doc.text(fiveYr, margin + sensColWidths[0] + sensColWidths[1] + 2, yPos + 4);
        yPos += sensRowH;
      });
      yPos += 10;

      // === PAGE 4: 5-YEAR FORECAST ===
      yPos = newPage();
      yPos = sectionTitle('5-Year Financial Forecast', yPos);
      yPos = addChartImage(chartImages.fiveYearForecast, yPos);
      const forecastHeaders = ['Year', 'Total Sales', 'Net Profit', 'Cumulative Net', 'ROI %'];
      const colWidths = [20, 40, 40, 45, 25];
      let xStart = margin;
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      const forecastRowH = 7;
      const forecastTableTop = yPos;
      doc.setFillColor(...primaryColor);
      doc.rect(margin, forecastTableTop, contentWidth, forecastRowH, 'F');
      doc.setTextColor(255, 255, 255);
      forecastHeaders.forEach((h, i) => {
        doc.text(h, xStart + 2, forecastTableTop + 4);
        xStart += colWidths[i];
      });
      yPos = forecastTableTop + forecastRowH;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...darkColor);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      this.fiveYearForecast.forEach((year, rowIdx) => {
        yPos = checkPage(yPos, forecastRowH);
        doc.setFillColor(rowIdx % 2 === 0 ? 255 : 248, rowIdx % 2 === 0 ? 255 : 248, rowIdx % 2 === 0 ? 255 : 248);
        doc.rect(margin, yPos, contentWidth, forecastRowH, 'FD');
        xStart = margin;
        [year.year, this.currency(year.totalSales).format(), this.currency(year.netProfit).format(), this.currency(year.cumulativeNet).format(), `${year.roi.toFixed(1)}%`].forEach((cell, i) => {
          doc.text(String(cell), xStart + 2, yPos + 4);
          if (i < colWidths.length - 1) {
            doc.line(xStart + colWidths[i], yPos, xStart + colWidths[i], yPos + forecastRowH);
          }
          xStart += colWidths[i];
        });
        yPos += forecastRowH;
      });
      yPos += 10;

      // === PAGE 5: ROI METRICS & CASH FLOW ===
      yPos = newPage();
      yPos = sectionTitle('Investment ROI & Operational Metrics', yPos);
      yPos = addChartImage(chartImages.cashFlow, yPos, 'Cumulative cash flow over 5 years');
      const roiRows = [
        ['3-Year Investment', this.currency(this.investment).format()],
        ['Annual Investment (Y1-3)', this.currency(this.annualSystemInvestment).format()],
        ['Annual Sales Increase', this.currency(this.avgAnnualIncreaseSales).format()],
        ['Annual Net Profit Increase', this.currency(this.avgNetAnnualIncreaseSales).format()],
        ['Credit Card Fee Savings', this.currency(this.annualCreditCardFees).format()],
        ['Total Annual Benefit', this.currency(this.avgNetAnnualIncreaseSales + this.annualCreditCardFees).format()],
        ['3-Year ROI', `${this.threeYearROI.toFixed(1)}%`],
        ['5-Year ROI', `${this.fiveYearROI.toFixed(1)}%`],
        ['Sales per Hour', this.currency(this.salesPerHour).format()],
        ['Labor Cost per Transaction', this.currency(this.annualLaborCost / this.totalAnnualTransactions || 0).format()],
        ['Credit Card Fee Rate (effective)', (this.numDailyCreditCardTransactions * 365 * this.avgDailySale) > 0 ? `${((this.annualCreditCardFees / (this.numDailyCreditCardTransactions * 365 * this.avgDailySale)) * 100).toFixed(2)}%` : 'N/A'],
        ['Net Profit Margin', (this.annualCafeteriaSales + this.avgAnnualIncreaseSales) > 0 ? `${((this.netProfit / (this.annualCafeteriaSales + this.avgAnnualIncreaseSales)) * 100).toFixed(2)}%` : 'N/A']
      ];
      const roiCol1 = 100;
      const roiRowH = 7;
      const roiTableTop = yPos;
      doc.setFontSize(9);
      doc.setFillColor(...primaryColor);
      doc.rect(margin, roiTableTop, contentWidth, roiRowH, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      doc.text('Metric', margin + roiCol1 - 4, roiTableTop + 4.5, { align: 'right' });
      doc.text('Value', margin + roiCol1 + 4, roiTableTop + 4.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...darkColor);
      yPos = roiTableTop + roiRowH;
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(margin + roiCol1, roiTableTop, margin + roiCol1, roiTableTop + roiRowH);
      roiRows.forEach(([label, value], i) => {
        yPos = checkPage(yPos, roiRowH);
        doc.setFillColor(i % 2 === 0 ? 255 : 248, i % 2 === 0 ? 255 : 248, i % 2 === 0 ? 255 : 248);
        doc.rect(margin, yPos, contentWidth, roiRowH, 'FD');
        doc.line(margin + roiCol1, yPos, margin + roiCol1, yPos + roiRowH);
        doc.setTextColor(...grayColor);
        doc.text(label, margin + roiCol1 - 2, yPos + 4.5, { align: 'right' });
        doc.setTextColor(...darkColor);
        doc.setFont(undefined, 'bold');
        doc.text(String(value), margin + roiCol1 + 2, yPos + 4.5);
        doc.setFont(undefined, 'normal');
        yPos += roiRowH;
      });
      yPos += 5;
      doc.setFontSize(8);
      doc.setTextColor(...grayColor);
      doc.text('Credit Card Fee Rate: effective rate from 2.9% + $0.30/txn. The flat fee raises the effective % on smaller tickets.', margin, yPos, { maxWidth: contentWidth });
      yPos += 10;

      // === PAGE 6: LABOR ANALYSIS (with traffic light) ===
      yPos = newPage();
      yPos = sectionTitle('Labor Analysis', yPos);
      doc.setFontSize(9);
      doc.setTextColor(...grayColor);
      doc.text('Industry benchmarks for foodservice labor costs. Your position vs. healthy ranges:', margin, yPos, { maxWidth: contentWidth });
      yPos += 12;

      // Traffic light: Labor Cost % vs benchmarks (Green <35, Yellow 35-40, Red >40)
      const laborPct = this.laborCostPrecent;
      const laborStatus = laborPct <= 35 ? 'green' : laborPct <= 40 ? 'yellow' : 'red';
      const laborStatusText = laborPct <= 35 ? 'Healthy' : laborPct <= 40 ? 'Borderline' : 'Review';
      const trafficLightW = 50;
      const trafficLightH = 18;
      yPos = checkPage(yPos, trafficLightH);
      doc.setFillColor(laborStatus === 'green' ? 34 : laborStatus === 'yellow' ? 234 : 220, laborStatus === 'green' ? 197 : laborStatus === 'yellow' ? 179 : 53, laborStatus === 'green' ? 94 : laborStatus === 'yellow' ? 8 : 47);
      doc.rect(margin, yPos, trafficLightW, trafficLightH, 'F');
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.3);
      doc.rect(margin, yPos, trafficLightW, trafficLightH, 'S');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(`Labor Cost: ${laborPct.toFixed(1)}%`, margin + trafficLightW / 2, yPos + 7, { align: 'center' });
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text(laborStatusText, margin + trafficLightW / 2, yPos + 14, { align: 'center' });
      doc.setTextColor(...darkColor);
      doc.setFontSize(9);
      doc.text(`Industry target: 25–35% (QSR/Casual). ${laborPct > 60 ? 'Prime Cost >60% — bold action recommended.' : 'Prime Cost <60% target.'}`, margin + trafficLightW + 8, yPos + 10, { maxWidth: contentWidth - trafficLightW - 16 });
      yPos += trafficLightH + 12;

      const laborCol1 = 100;
      const laborRowH = 7;
      const laborTableTop = yPos;
      const laborRows = [
        ['Labor Cost %', `${this.laborCostPrecent.toFixed(1)}%`],
        ['Food Cost (COGS) %', `${this.foodCostPrecent}%`],
        ['Prime Cost (Labor + COGS)', this.currency(this.primeCost).format()],
        ['Prime Cost %', `${this.primeCostPrecent.toFixed(1)}%`]
      ];
      doc.setFillColor(...primaryColor);
      doc.rect(margin, laborTableTop, contentWidth, laborRowH, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      doc.text('Metric', margin + laborCol1 - 4, laborTableTop + 4.5, { align: 'right' });
      doc.text('Value', margin + laborCol1 + 4, laborTableTop + 4.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...darkColor);
      yPos = laborTableTop + laborRowH;
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(margin + laborCol1, laborTableTop, margin + laborCol1, laborTableTop + laborRowH);
      laborRows.forEach(([label, value], i) => {
        yPos = checkPage(yPos, laborRowH);
        doc.setFillColor(i % 2 === 0 ? 255 : 248, i % 2 === 0 ? 255 : 248, i % 2 === 0 ? 255 : 248);
        doc.rect(margin, yPos, contentWidth, laborRowH, 'FD');
        doc.line(margin + laborCol1, yPos, margin + laborCol1, yPos + laborRowH);
        doc.setTextColor(...grayColor);
        doc.text(label, margin + laborCol1 - 2, yPos + 4.5, { align: 'right' });
        doc.setTextColor(...darkColor);
        doc.setFont(undefined, 'bold');
        if (label === 'Prime Cost %' && this.primeCostPrecent > 60) {
          doc.setTextColor(220, 53, 47);
        }
        doc.text(String(value), margin + laborCol1 + 2, yPos + 4.5);
        doc.setTextColor(...darkColor);
        doc.setFont(undefined, 'normal');
        yPos += laborRowH;
      });
      yPos += 10;

      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(...primaryColor);
      doc.text('Industry Benchmarks', margin, yPos);
      yPos += 7;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...darkColor);
      const benchRows = [
        ['Quick Service (QSR)', '25% – 30%'],
        ['Casual Dining', '30% – 35%'],
        ['Fine Dining', '35% – 40%']
      ];
      benchRows.forEach(([type, range]) => {
        doc.setTextColor(...grayColor);
        doc.text(type, margin + 90 - 2, yPos + 4, { align: 'right' });
        doc.setTextColor(...darkColor);
        doc.text(range, margin + 92, yPos + 4);
        yPos += 6;
      });
      yPos += 8;
      doc.setFontSize(8);
      doc.setTextColor(...grayColor);
      doc.text('Formula: Labor Cost % = (Total Labor Costs ÷ Gross Sales) × 100', margin, yPos);

      // === PAGE 7: STRATEGIC RECOMMENDATIONS ===
      yPos = newPage();
      yPos = sectionTitle('Strategic Recommendations', yPos);
      doc.setFontSize(9);
      const recommendations = [
        this.participationCount < 50 ? `• Increase participation from ${this.participationCount}% to 60%+ to unlock additional revenue potential` : null,
        this.checkIncreasePrecent < 10 ? '• Implement upselling strategies to increase average ticket by 5-10%' : null,
        '• Leverage zero-fee cashless transactions to reduce credit card processing costs',
        this.laborCostPrecent >= 35 ? '• Utilize automated workflows to reduce labor overhead (your labor cost supports this)' : '• Utilize automated workflows to reduce labor overhead',
        '• Focus on incremental improvements: small changes compound significantly over 5 years',
        '• Monitor participation rates and adjust marketing/engagement strategies accordingly'
      ].filter(Boolean);
      recommendations.forEach((rec) => {
        yPos = checkPage(yPos);
        doc.setTextColor(...darkColor);
        doc.text(rec, margin, yPos, { maxWidth: contentWidth });
        yPos += 7;
      });

      // === FOUNDER'S NOTE (shortened, personal) ===
      yPos = newPage();
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 45, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('A Note from Our Founder', pageWidth / 2, 22, { align: 'center' });
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(255, 248, 240);
      doc.text('Wolf Scott, Founder — Newcare International', pageWidth / 2, 35, { align: 'center' });

      yPos = 60;
      doc.setTextColor(...darkColor);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const founderLines = [
        'Thank you for exploring what cashless dining can do for your operation. We built Newcare on a simple belief: your success is our success.',
        '',
        'We don\'t just sell software—we partner with you. Whether you\'re exploring options or ready to take the next step, we\'re here to help.',
        '',
        'Reach out anytime. We\'d be honored to be part of your story.',
        '',
        'With respect and partnership,',
        '',
        'Wolf Scott',
        'Founder, Newcare International',
        'www.newcare.io  •  cashlessFyi@newcare.io'
      ];

      founderLines.forEach((line) => {
        yPos = checkPage(yPos, 8);
        if (line === '') {
          yPos += 4;
        } else {
          const lines = doc.splitTextToSize(line, contentWidth);
          doc.text(lines, margin, yPos);
          yPos += lines.length * 5 + 2;
        }
      });

      // === APPENDIX: WORKSHEET INPUT VALUES ===
      yPos = newPage();
      yPos = sectionTitle('Appendix: Worksheet Input Values', yPos);

      const inputRows = [
        ['Accounts / Employees #', this.numEmployees],
        ['Total Annual Sales $', this.currency(this.annualCafeteriaSales).format()],
        ['Daily Transactions #', this.numDailyTransactions],
        ['# Credit Card Transactions', this.creditCardTransactions],
        ['Credit Card Period', this.creditCardTransactionsPeriod === 'daily' ? 'Per Day' : this.creditCardTransactionsPeriod === 'weekly' ? 'Per Week' : 'Per Month'],
        ['Average Transaction Value $', this.currency(this.avgDailySale).format()],
        ['In-House Charge Solution', this.hasInHouseCharge ? 'Yes' : 'No'],
        ['Daily Charge Transactions #', this.numDailyChargeTransactions],
        ['Cashless Participation %', `${this.participationCount}%`],
        ['3-Year Investment $', this.currency(this.investment).format()],
        ['Traffic Increase %', `${this.trafficIncreasePrecent}%`],
        ['Check Increase %', `${this.checkIncreasePrecent}%`],
        ['Weekly Operating Hours', this.weeklyHours],
        ['# Foodservice Staff', this.numFoodserviceStaff],
        ['Average Hourly Wage $', this.currency(this.averageWage).format()],
        ['Food Cost %', `${this.foodCostPrecent}%`],
        ['Additional Overhead $', this.currency(this.additionalOverhead).format()],
        ['Overhead Period', this.additionalOverheadPeriod === 'daily' ? 'Daily' : this.additionalOverheadPeriod === 'monthly' ? 'Monthly' : 'Annual']
      ];

      const col1Width = 100;
      const rowHeight = 7;
      const tableTop = yPos;

      doc.setFontSize(9);
      doc.setFillColor(...primaryColor);
      doc.rect(margin, tableTop, contentWidth, rowHeight, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      doc.text('Input', margin + col1Width - 4, tableTop + 4.5, { align: 'right' });
      doc.text('Value', margin + col1Width + 4, tableTop + 4.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...darkColor);
      yPos = tableTop + rowHeight;

      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(margin + col1Width, tableTop, margin + col1Width, tableTop + rowHeight);
      inputRows.forEach(([label, value], i) => {
        yPos = checkPage(yPos, rowHeight);
        doc.setFillColor(i % 2 === 0 ? 255 : 248, i % 2 === 0 ? 255 : 248, i % 2 === 0 ? 255 : 248);
        doc.rect(margin, yPos, contentWidth, rowHeight, 'FD');
        doc.line(margin + col1Width, yPos, margin + col1Width, yPos + rowHeight);
        doc.setTextColor(...grayColor);
        doc.text(label, margin + col1Width - 2, yPos + 4.5, { align: 'right' });
        doc.setTextColor(...darkColor);
        doc.setFont(undefined, 'bold');
        doc.text(String(value), margin + col1Width + 2, yPos + 4.5);
        doc.setFont(undefined, 'normal');
        yPos += rowHeight;
      });
      yPos += 8;

      // Footer on all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(...grayColor);
        doc.text(`Page ${i} of ${pageCount} • cashless.fyi • www.newcare.io`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }

      doc.save(`ROI-Analysis-Report-${new Date().toISOString().split('T')[0]}.pdf`);

      // Restore charts to normal (dark theme) display
      this.renderAnalysisCharts(false);
      if (this.$refs.costPieChart) {
        this.$refs.costPieChart.renderChart(this.costBreakdownChartData, this.pieChartOptions);
      }
    },
    completeWizard() {
      // Map wizard data to calculator form
      this.annualCafeteriaSales = this.wizardData.annualCafeteriaSales || 0;
      this.numEmployees = this.wizardData.numEmployees || 0;
      this.foodCostPrecent = this.wizardData.foodCostPrecent || 0;
      this.numFoodserviceStaff = this.wizardData.numFoodserviceStaff || 0;
      this.averageWage = this.wizardData.averageWage || 0;
      this.weeklyHours = this.wizardData.weeklyHours || 98;
      this.hasInHouseCharge = this.wizardData.hasInHouseCharge || false;
      
      // Set defaults based on whether they have in-house charge
      // If they DON'T have cashless: default to 20% traffic, 10% check
      // If they DO have cashless: default to 5% traffic, 2% check
      if (this.hasInHouseCharge) {
        this.checkIncreasePrecent = this.wizardData.checkIncreasePrecent || 2;
        this.trafficIncreasePrecent = this.wizardData.trafficIncreasePrecent || 5;
      } else {
        this.checkIncreasePrecent = this.wizardData.checkIncreasePrecent || 10;
        this.trafficIncreasePrecent = this.wizardData.trafficIncreasePrecent || 20;
      }
      
      // Handle credit card transactions with period conversion
      this.creditCardTransactions = this.wizardData.creditCardTransactions;
      this.creditCardTransactionsPeriod = this.wizardData.creditCardTransactionsPeriod || 'weekly';
      
      // If credit card transactions not set, default to 50% of daily transactions in weekly terms
      if (this.creditCardTransactions === null || this.creditCardTransactions === undefined) {
        const dailyTx = this.wizardData.numDailyTransactions || 0;
        // 50% of daily transactions, converted to weekly (daily * 0.5 * 7)
        this.creditCardTransactions = dailyTx * 0.5 * 7;
        this.creditCardTransactionsPeriod = 'weekly';
      }
      
      this.additionalOverhead = this.wizardData.additionalOverhead || 0;
      this.additionalOverheadPeriod = this.wizardData.additionalOverheadPeriod || 'annual';
      
      // Calculate average transaction value if not provided
      const calculatedValue = this.wizardAvgDailySale;
      
      // If user provided a value in wizard, use it; otherwise use calculated
      if (this.wizardData.avgTransactionValue !== null && this.wizardData.avgTransactionValue !== undefined && this.wizardData.avgTransactionValue !== 0) {
        this.avgTransactionValue = this.wizardData.avgTransactionValue;
        this.userAvgTransactionValue = this.wizardData.avgTransactionValue;
      } else {
        // Auto-populate with calculated value
        this.avgTransactionValue = calculatedValue;
        this.userAvgTransactionValue = null; // No user input, so use calculated
      }
      
      // Use total daily transactions from wizard - this is the permanent/base number
      // Never calculate this from credit card transactions - the math goes the other way
      this.numDailyTransactions = this.wizardData.numDailyTransactions || 0;
      
      // Get charge transactions if in-house solution exists
      const chargeTx = this.hasInHouseCharge ? (this.wizardData.numDailyChargeTransactions || 0) : 0;
      
      // Calculate participation % (charge transactions / total transactions)
      if (this.numDailyTransactions > 0 && chargeTx > 0) {
        this.participationCount = Currency(chargeTx).divide(this.numDailyTransactions).multiply(100).value || 0;
      } else {
        this.participationCount = 0;
      }
      
      // Charge transactions only when an in-house system exists today.
      // Always keep the 3-year investment (needed for payback even when starting from cash).
      this.numDailyChargeTransactions = chargeTx;
      this.investment = this.wizardData.investment || 0;
      
      // Set additional overhead
      this.additionalOverhead = this.wizardData.additionalOverhead || 0;
      this.additionalOverheadPeriod = this.wizardData.additionalOverheadPeriod || 'annual';
            
      this.showWizard = false;
      this.updateCharts();
    },
    // Handle wizard being closed (clicking outside, ESC key, etc.)
    // Transfer any entered data to the calculator form
    onWizardHide() {
      // Only transfer data if wizard has been interacted with
      // Check if any key fields have been filled
      const hasData = this.wizardData.annualCafeteriaSales > 0 || 
                      this.wizardData.numEmployees > 0 || 
                      this.wizardData.numDailyTransactions > 0;
      
      if (hasData) {
        // Transfer wizard data to calculator form (same logic as completeWizard)
        this.annualCafeteriaSales = this.wizardData.annualCafeteriaSales || 0;
        this.numEmployees = this.wizardData.numEmployees || 0;
        this.foodCostPrecent = this.wizardData.foodCostPrecent || 0;
        this.numFoodserviceStaff = this.wizardData.numFoodserviceStaff || 0;
        this.averageWage = this.wizardData.averageWage || 0;
        this.weeklyHours = this.wizardData.weeklyHours || 98;
        this.hasInHouseCharge = this.wizardData.hasInHouseCharge || false;
        
        // Set defaults based on whether they have in-house charge
        if (this.hasInHouseCharge) {
          this.checkIncreasePrecent = this.wizardData.checkIncreasePrecent || 2;
          this.trafficIncreasePrecent = this.wizardData.trafficIncreasePrecent || 5;
        } else {
          this.checkIncreasePrecent = this.wizardData.checkIncreasePrecent || 10;
          this.trafficIncreasePrecent = this.wizardData.trafficIncreasePrecent || 20;
        }
        
        // Handle credit card transactions with period conversion
        this.creditCardTransactions = this.wizardData.creditCardTransactions;
        this.creditCardTransactionsPeriod = this.wizardData.creditCardTransactionsPeriod || 'weekly';
        
        // If credit card transactions not set, default to 50% of daily transactions in weekly terms
        if (this.creditCardTransactions === null || this.creditCardTransactions === undefined) {
          const dailyTx = this.wizardData.numDailyTransactions || 0;
          // 50% of daily transactions, converted to weekly (daily * 0.5 * 7)
          this.creditCardTransactions = dailyTx * 0.5 * 7;
          this.creditCardTransactionsPeriod = 'weekly';
        }
        
        this.additionalOverhead = this.wizardData.additionalOverhead || 0;
        this.additionalOverheadPeriod = this.wizardData.additionalOverheadPeriod || 'annual';
        
        // Calculate average transaction value if not provided
        const calculatedValue = this.wizardAvgDailySale;
        
        // If user provided a value in wizard, use it; otherwise use calculated
        if (this.wizardData.avgTransactionValue !== null && this.wizardData.avgTransactionValue !== undefined && this.wizardData.avgTransactionValue !== 0) {
          this.avgTransactionValue = this.wizardData.avgTransactionValue;
          this.userAvgTransactionValue = this.wizardData.avgTransactionValue;
        } else {
          // Auto-populate with calculated value
          this.avgTransactionValue = calculatedValue;
          this.userAvgTransactionValue = null; // No user input, so use calculated
        }
        
        // Use total daily transactions from wizard - this is the permanent/base number
        this.numDailyTransactions = this.wizardData.numDailyTransactions || 0;
        
        // Get charge transactions if in-house solution exists
        const chargeTx = this.hasInHouseCharge ? (this.wizardData.numDailyChargeTransactions || 0) : 0;
        
        // Calculate participation % (charge transactions / total transactions)
        if (this.numDailyTransactions > 0 && chargeTx > 0) {
          this.participationCount = Currency(chargeTx).divide(this.numDailyTransactions).multiply(100).value || 0;
        } else {
          this.participationCount = 0;
        }
        
        // Set charge transactions and investment
        this.numDailyChargeTransactions = chargeTx;
        this.investment = this.wizardData.investment || 0;
        
        // Set additional overhead
        this.additionalOverhead = this.wizardData.additionalOverhead || 0;
        this.additionalOverheadPeriod = this.wizardData.additionalOverheadPeriod || 'annual';
        
        this.updateCharts();
      }
    },
    restartWizard() {
      // Map calculator form data back to wizard format
      // Note: Some fields like numDailyCreditCardTransactions need to be derived
      // For now, we'll preserve what we can and let user re-enter if needed
      this.wizardData.annualCafeteriaSales = this.annualCafeteriaSales || 0;
      this.wizardData.numEmployees = this.numEmployees || 0;
      this.wizardData.foodCostPrecent = this.foodCostPrecent || 0;
      this.wizardData.numFoodserviceStaff = this.numFoodserviceStaff || 0;
      this.wizardData.averageWage = this.averageWage || 0;
      this.wizardData.weeklyHours = this.weeklyHours || 98;
      this.wizardData.checkIncreasePrecent = this.checkIncreasePrecent || 0;
      this.wizardData.trafficIncreasePrecent = this.trafficIncreasePrecent || 0;
      this.wizardData.hasInHouseCharge = this.hasInHouseCharge || false;
      this.wizardData.numDailyChargeTransactions = this.numDailyChargeTransactions || 0;
      this.wizardData.investment = this.investment || 0;
      this.wizardData.additionalOverhead = this.additionalOverhead || 0;
      this.wizardData.additionalOverheadPeriod = this.additionalOverheadPeriod || 'annual';
      // Restore the originally entered average ticket, if we have it
      this.wizardData.avgTransactionValue = this.userAvgTransactionValue || this.avgTransactionValue;
      // Restore credit card transactions and period
      this.wizardData.creditCardTransactions = this.creditCardTransactions;
      this.wizardData.creditCardTransactionsPeriod = this.creditCardTransactionsPeriod || 'weekly';
      this.showWizard = true;
    },
  },
  mounted() {
    // Open wizard on first load to guide users through data entry
    this.showWizard = true;
    // Initialize credit card transactions default
    this.$nextTick(() => {
      this.updateWizardCreditCardDefault();
    });
  },
  watch: {
    // When wizard opens, initialize credit card transactions default
    showWizard(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.updateWizardCreditCardDefault();
        });
      }
    },
    // Reactive updates when calculator form values change
    annualCafeteriaSales() {
      this.updateCharts();
      // If user hasn't entered a value, auto-populate avgTransactionValue
      if (this.userAvgTransactionValue === null && this.calculatedAvgTransactionValue > 0) {
        this.avgTransactionValue = this.calculatedAvgTransactionValue;
      }
    },
    numDailyTransactions() {
      this.updateCharts();
      // If user hasn't entered a value, auto-populate avgTransactionValue
      if (this.userAvgTransactionValue === null && this.calculatedAvgTransactionValue > 0) {
        this.avgTransactionValue = this.calculatedAvgTransactionValue;
      }
    },
    numDailyChargeTransactions() {
      this.updateCharts();
    },
    // When hasInHouseCharge changes, update default values for traffic and check increases
    hasInHouseCharge(newVal, oldVal) {
      // Only auto-fill defaults if both fields are blank/zero
      // If they DON'T have cashless: default to 20% traffic, 10% check
      // If they DO have cashless: default to 5% traffic, 2% check
      if (oldVal !== undefined && newVal !== oldVal) {
        const noTraffic = !this.trafficIncreasePrecent;
        const noCheck = !this.checkIncreasePrecent;
        if (noTraffic && noCheck) {
          if (newVal === false) {
            // No existing cashless system
            this.trafficIncreasePrecent = 20;
            this.checkIncreasePrecent = 10;
          } else {
            // Has existing cashless system
            this.trafficIncreasePrecent = 5;
            this.checkIncreasePrecent = 2;
          }
        }
      }
      this.updateCharts();
    },
    participationCount() {
      this.updateCharts();
    },
    foodCostPrecent() {
      this.updateCharts();
    },
    checkIncreasePrecent() {
      this.updateCharts();
    },
    trafficIncreasePrecent() {
      this.updateCharts();
    },
    investment() {
      this.updateCharts();
    },
    numFoodserviceStaff() {
      this.updateCharts();
    },
    averageWage() {
      this.updateCharts();
    },
    additionalOverhead() {
      this.updateCharts();
    },
    additionalOverheadPeriod() {
      this.updateCharts();
    },
    creditCardTransactions() {
      this.updateCharts();
    },
    creditCardTransactionsPeriod() {
      this.updateCharts();
    },
    costBreakdownChartData: {
      handler() {
        this.updateCharts();
      },
      deep: true,
    },
    salesChartData: {
      handler() {
        this.updateCharts();
      },
      deep: true,
    },
    roiChartData: {
      handler() {
        this.updateCharts();
      },
      deep: true,
    },
    fiveYearForecastData: {
      handler() {
        this.updateCharts();
      },
      deep: true,
    },
    sensitivityAnalysisData: {
      handler() {
        this.updateCharts();
      },
      deep: true,
    },
    cumulativeCashFlowData: {
      handler() {
        this.updateCharts();
      },
      deep: true,
    },
    // Auto-populate avgTransactionValue when blank and we have data to calculate
    calculatedAvgTransactionValue() {
      if ((this.avgTransactionValue === null || this.avgTransactionValue === undefined || this.avgTransactionValue === 0) && 
          this.calculatedAvgTransactionValue > 0 && this.userAvgTransactionValue === null) {
        this.avgTransactionValue = this.calculatedAvgTransactionValue;
      }
    },
    // Track user input for average transaction value
    avgTransactionValue(newVal, oldVal) {
      // If user is typing a value (not null/undefined/0), store it as user input
      if (newVal !== null && newVal !== undefined && newVal !== 0) {
        this.userAvgTransactionValue = newVal;
        
        // If user manually entered a value, update numDailyTransactions based on it
        // Formula: numDailyTransactions = annualCafeteriaSales / (avgTransactionValue * 365)
        if (this.annualCafeteriaSales > 0 && newVal > 0) {
          const calculatedDailyTx = this.annualCafeteriaSales / (newVal * 365);
          // Only update if the change is significant (more than 1 transaction difference)
          // This prevents infinite loops and only updates when user actually changes the value
          if (Math.abs(calculatedDailyTx - this.numDailyTransactions) > 1) {
            this.numDailyTransactions = Math.round(calculatedDailyTx);
          }
        }
      } else if (newVal === null || newVal === undefined || newVal === 0) {
        // If cleared, reset user input and use calculated
        this.userAvgTransactionValue = null;
        if (this.calculatedAvgTransactionValue > 0) {
          this.avgTransactionValue = this.calculatedAvgTransactionValue;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/app.scss';

.calculator-container {
  width: 100%;
  max-width: 100%;
  padding: 0;
  min-height: calc(100vh - 120px);
}

.calculator-header {
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid rgba($body-color, 0.1);
  background: lighten($body-bg, 3%);
  flex-shrink: 0;

  .calculator-title {
    font-size: 2rem;
    font-weight: 700;
    color: $body-color;
    margin: 0;
  }
}

.calculator-main {
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 500px;
}

// Main tabs - content expands; single page scroll only (no column scrollbars)
.main-tabs {
  display: flex;
  flex-direction: column;

  ::v-deep .nav-tabs {
    border-bottom: 2px solid rgba($body-color, 0.2);
    margin-bottom: 0;
    padding: 0 2rem;
    background: lighten($body-bg, 3%);
    flex-shrink: 0;

    .nav-item {
      .nav-link {
        color: rgba($body-color, 0.7);
        background: transparent;
        border: none;
        border-bottom: 3px solid transparent;
        padding: 1rem 2rem;
        font-size: 1.1rem;
        font-weight: 600;
        transition: all 0.2s ease;

        &:hover {
          color: $body-color;
          border-bottom-color: rgba($primary, 0.5);
        }

        &.active {
          color: $primary;
          background: transparent;
          border-bottom-color: $primary;
        }
      }
    }
  }

  ::v-deep .tab-content.calculator-tabs-content {
    padding: 0;
  }
}

// Inputs container - columns expand with content; single page scroll only
.inputs-container {
  width: 100%;
  padding: 0;

  .row {
    align-items: stretch;
  }
}

.inputs-panel {
  padding: 1.5rem 2rem;
  background: lighten($body-bg, 2%);
  min-height: 400px;
}

.metrics-panel {
  padding: 1.5rem 2rem;
  background: lighten($body-bg, 4%);
  border-left: 1px solid rgba($body-color, 0.1);
  min-height: 400px;
}

.input-section {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.1);
  border-radius: 0.375rem;
  padding: 1.25rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  .section-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba($primary, 0.2);
  }
}

.form-field {
  margin-bottom: 1.25rem;
  text-align: left;

  label {
    display: block;
    font-size: 0.875rem;
    font-weight: 600;
    color: $body-color;
    margin-bottom: 0.375rem;
    text-align: left;
  }

    .input-field {
      width: 100%;
      max-width: 220px;
      background: lighten($body-bg, 10%) !important;
      color: $body-color !important;
      border: 1px solid rgba($body-color, 0.25) !important;
      border-radius: 0.25rem;
      padding: 0.5rem 0.75rem;
      font-size: 0.9rem;
      transition: all 0.2s ease;

      &:focus {
        background: lighten($body-bg, 15%) !important;
        color: $body-color !important;
        border-color: $primary !important;
        box-shadow: 0 0 0 0.15rem rgba($primary, 0.2) !important;
        outline: none;
      }

      &::placeholder {
        color: rgba($body-color, 0.5) !important;
      }
    }

    .checkbox-field {
      margin-bottom: 0.5rem;
      
      ::v-deep .custom-control-label {
        color: $body-color !important;
        font-weight: 600;
        font-size: 0.95rem;
        cursor: pointer;
      }

      ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
        background-color: $primary;
        border-color: $primary;
      }
    }

    .overhead-period-radio {
      margin-top: 0.5rem;
      
      ::v-deep .custom-control {
        margin-right: 1rem;
        margin-bottom: 0.5rem;
        display: inline-block;
        
        .custom-control-label {
          color: $body-color !important;
          font-weight: 500;
          font-size: 0.9rem;
          cursor: pointer;
          padding-left: 0.5rem;
        }
        
        .custom-control-input:checked ~ .custom-control-label {
          color: $primary !important;
        }
        
        .custom-control-input:checked ~ .custom-control-label::before {
          background-color: $primary;
          border-color: $primary;
        }
      }
    }

    .calculated-value {
      font-size: 1.1rem;
      font-weight: 700;
      color: $primary;
      padding: 0.625rem 0;
      text-align: left;
    }

    .field-description {
      display: block;
      font-size: 0.8rem;
      color: rgba($body-color, 0.65);
      margin-top: 0.375rem;
      line-height: 1.3;
      text-align: left;
    }

    .delta-notice {
      margin-top: 0.5rem;
      padding: 0.5rem;
      background-color: rgba($warning, 0.1);
      border-left: 3px solid $warning;
      border-radius: 4px;
      font-size: 0.85rem;

      .delta-label {
        color: rgba($body-color, 0.8);
        margin-right: 0.5rem;
      }

      .delta-value {
        font-weight: 600;
        color: $body-color;
        margin-right: 0.5rem;

        &.calculated {
          color: $info;
        }
      }

      .delta-diff {
        font-weight: 600;

        &.positive {
          color: $success;
        }

        &.negative {
          color: $danger;
        }
      }
    }

    .input-field.has-delta {
      border-color: $warning;
      box-shadow: 0 0 0 0.2rem rgba($warning, 0.25);
    }
  }

  .no-cashless-note {
    margin-top: 1rem;
    padding: 1rem;
    background: lighten($body-bg, 8%);
    border: 1px solid rgba($primary, 0.3);
    border-radius: 0.375rem;
    border-left: 3px solid $primary;

    p {
      margin-bottom: 0.75rem;
      color: $body-color;
      font-weight: 600;
    }

    ul {
      margin: 0;
      padding-left: 1.5rem;
      color: rgba($body-color, 0.9);

      li {
        margin-bottom: 0.5rem;
        line-height: 1.5;

        strong {
          color: $primary;
        }
      }
    }
  }

.actions-bar {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  padding-bottom: 1rem;
  border-top: 1px solid rgba($body-color, 0.1);
  text-align: left;
}

// Key Results Section
.key-results-section {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.1);
  border-radius: 0.375rem;
  padding: 1.25rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  .section-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 1.25rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba($primary, 0.2);
  }
}

.key-result-card {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.15);
  border-radius: 0.375rem;
  padding: 1.25rem;
  text-align: left;
  margin-bottom: 1rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  height: 100%;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  }

  &.highlight-card {
    border-color: rgba($primary, 0.3);
    background: lighten($body-bg, 7%);
  }
  
  &.financial-start {
    background: lighten($body-bg, 8%);
    margin-bottom: 1.5rem;
  }
  
  &.financial-cost {
    border-left: 3px solid rgba($danger, 0.5);
  }
  
  &.financial-result {
    background: lighten($body-bg, 8%);
    margin-top: 1rem;
  }

  .key-result-label {
    font-size: 0.8rem;
    color: rgba($body-color, 0.7);
    margin-bottom: 0.5rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .key-result-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: $body-color;
    margin-bottom: 0.375rem;
    line-height: 1.2;

    &.highlight {
      color: $primary;
    }
  }

  .key-result-note {
    font-size: 0.75rem;
    color: rgba($body-color, 0.6);
    line-height: 1.3;
  }
}

// Financial statement format
.financial-statement {
  .financial-line {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 1rem 0;
    border-bottom: 1px solid rgba($body-color, 0.1);
    
    .financial-label {
      flex: 1;
      font-size: 0.95rem;
      font-weight: 600;
      color: $body-color;
      text-align: right; // Right-justify prompts/labels
      padding-right: 1.5rem;
    }
    
    .financial-value {
      font-size: 1.1rem;
      font-weight: 700;
      color: $body-color;
      text-align: right;
      min-width: 140px;
      
      &.highlight {
        color: $primary;
      }
    }
    
    .financial-note {
      position: absolute;
      left: 0;
      margin-top: 1.5rem;
      font-size: 0.75rem;
      color: rgba($body-color, 0.6);
      text-align: right;
      // Align with the label column (same width as label)
      width: calc(100% - 140px); // Match the label column width
      padding-right: 1.5rem; // Match label padding
    }
    
    &.financial-start {
      padding: 1.25rem 0;
      border-bottom: 2px solid rgba($primary, 0.3);
      
      .financial-label {
        font-size: 1.1rem;
        font-weight: 700;
        color: $primary;
      }
      
      .financial-value {
        font-size: 1.5rem;
        font-weight: 700;
        color: $primary;
      }
    }
    
    &.financial-add {
      padding-left: 1rem;
      border-left: 2px solid rgba($success, 0.3);
      
      .financial-label {
        color: rgba($body-color, 0.9);
      }
      
      .financial-value {
        color: $success;
      }
    }
    
    &.financial-cost {
      padding-left: 1rem;
      border-left: 2px solid rgba($danger, 0.3);
      
      .financial-label {
        color: rgba($body-color, 0.9);
      }
    }
    
    &.financial-result {
      padding: 1.5rem 0;
      border-top: 2px solid rgba(74, 168, 146, 0.5);
      border-bottom: 2px solid rgba(74, 168, 146, 0.5);
      margin-top: 0.5rem;
      background: lighten($body-bg, 6%);
      
      .financial-label {
        font-size: 1.1rem;
        font-weight: 700;
        color: rgba(74, 168, 146, 1);
      }
      
      .financial-value {
        font-size: 1.5rem;
        font-weight: 700;
        color: rgba(74, 168, 146, 1);
      }
    }
    
    &.financial-metric {
      .financial-value {
        color: $primary;
      }
    }
  }
  
  .financial-divider {
    height: 1px;
    background: rgba($body-color, 0.2);
    margin: 1rem 0;
  }
}

.chart-card-small {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.15);
  border-radius: 0.375rem;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  h5 {
    font-size: 1rem;
    font-weight: 600;
    color: $body-color;
    margin-bottom: 1rem;
    text-align: center;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid rgba($body-color, 0.1);
  }

  canvas {
    max-height: 180px;
    max-width: 100%;
    margin: 0 auto;
    display: block;
  }
}

.savings-summary {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.15);
  border-radius: 0.375rem;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  h5 {
    font-size: 1rem;
    font-weight: 600;
    color: $body-color;
    margin-bottom: 1.25rem;
    text-align: center;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid rgba($body-color, 0.1);
  }

  .savings-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 0;
    border-bottom: 1px solid rgba($body-color, 0.1);

    &:last-child {
      border-bottom: none;
    }

    .savings-label {
      font-size: 0.95rem;
      color: rgba($body-color, 0.8);
      font-weight: 500;
    }

    .savings-value {
      font-size: 1.25rem;
      font-weight: 700;
      color: $body-color;

      &.highlight {
        color: $primary;
        font-size: 1.5rem;
      }
    }
  }

  .savings-note {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid rgba($body-color, 0.2);
    font-size: 0.875rem;
    color: rgba($body-color, 0.7);
    line-height: 1.5;
    font-style: italic;
  }
}

// Results container
.results-container {
  width: 100%;
  padding: 0;
}

.wizard-modal {
  .modal-content {
    background: lighten($body-bg, 5%);
    color: $body-color;
    border: 1px solid rgba($body-color, 0.2);
  }
  .modal-header {
    background: lighten($body-bg, 10%);
    color: $body-color;
  }

  // Override vue-form-wizard styles for better contrast
  ::v-deep .wizard-header {
    background: transparent;
    text-align: center;
    width: 100%;

    .wizard-title {
      display: block;
      width: 100%;
      text-align: center;
      margin: 0 auto;
      
      &::after {
        content: "Provide details about your location—a detailed worksheet will be generated at the end.";
        display: block;
        font-size: 0.9rem;
        font-weight: normal;
        color: rgba($body-color, 0.8);
        margin-top: 0.5rem;
        line-height: 1.4;
        text-align: center;
        width: 100%;
      }
    }

    // Hide the default subtitle/description
    .wizard-subtitle,
    .wizard-description,
    > p {
      display: none !important;
    }
  }

  ::v-deep .wizard-nav {
    .wizard-nav-item {
      .wizard-nav-link {
        color: rgba($body-color, 0.6) !important;
        background: transparent !important;

        &.active {
          color: $body-color !important;
          background: transparent !important;
        }

        .wizard-nav-link-inner {
          background: lighten($body-bg, 10%) !important;
          color: $body-color !important;
          border-color: rgba($body-color, 0.3) !important;
        }

        &.active .wizard-nav-link-inner {
          background: $primary !important;
          color: $body-bg !important;
          border-color: $primary !important;
        }
      }
    }
  }

  .wizard-intro {
    padding: 1rem 0;
    line-height: 1.7;
    color: $body-color;

    h4 {
      color: $primary;
      font-weight: 700;
    }

    p {
      margin-bottom: 1rem;
      color: rgba($body-color, 0.9);
    }
  }

  .wizard-field-tip {
    cursor: help;
    font-size: 0.9rem;
    opacity: 0.7;
    vertical-align: middle;

    &:hover {
      opacity: 1;
    }
  }

  ::v-deep .wizard-tab-content {
    color: $body-color;
  }

  ::v-deep .wizard-card-footer {
    background: transparent;
  }
}


// Wizard form styling - ensure proper formatting and contrast
.wizard-modal {
  .form-group {
    margin-bottom: 1.5rem;
    
    label {
      color: $body-color !important;
      font-weight: 600;
      font-size: 1rem;
      margin-bottom: 0.5rem;
      display: block;
    }
    
    .form-control,
    input[type="text"],
    input[type="number"] {
      background: lighten($body-bg, 10%) !important;
      color: $body-color !important;
      border: 1px solid rgba($body-color, 0.3) !important;
      border-radius: 0.5rem;
      padding: 0.75rem 1rem;
      font-size: 1rem;
      width: 100%;
      max-width: 400px;
      transition: all 0.2s ease;
      
      &:focus {
        background: lighten($body-bg, 15%) !important;
        color: $body-color !important;
        border-color: $primary !important;
        box-shadow: 0 0 0 0.2rem rgba($primary, 0.25) !important;
        outline: none;
      }
      
      &::placeholder {
        color: rgba($body-color, 0.5) !important;
      }
    }
    
    .form-text,
    small.form-text {
      color: rgba($body-color, 0.8) !important;
      font-size: 0.9rem;
      margin-top: 0.5rem;
      display: block;
    }
    
    .form-control-plaintext {
      color: $body-color !important;
      font-size: 1.1rem;
      font-weight: 600;
      padding: 0.75rem 0;
    }
    
    .custom-radio {
      margin-right: 1.5rem;
      
      label {
        color: $body-color !important;
        font-weight: 500;
        cursor: pointer;
      }
      
      input[type="radio"] {
        margin-right: 0.5rem;
      }
    }
  }
}

.results-note {
  font-size: 0.95rem;
  color: $secondary;
  margin-bottom: 2rem;
  padding: 1rem;
  background: lighten($body-bg, 5%);
  border-left: 3px solid $primary;
  border-radius: 0.25rem;
}

.results-tabs-content {
  padding: 0;
}

.results-grid {
  margin-top: 1.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.result-card {
  background: lighten($body-bg, 5%);
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba($body-color, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  h4 {
    font-size: 1.25rem;
    color: $body-color;
    margin-bottom: 1rem;
    font-weight: 600;
  }

  .value {
    font-size: 2rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 0.5rem;
  }

  .note {
    font-size: 0.9rem;
    color: rgba($body-color, 0.7);
    line-height: 1.4;
  }
}

.chart-card {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.1);
  border-radius: 0.375rem;
  padding: 1.25rem;
  margin-bottom: 1.25rem;
  min-height: 300px;
  position: relative;
  
  canvas {
    max-width: 100%;
  }
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  width: 100%;

  h4 {
    font-size: 1.1rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba($primary, 0.2);
    text-align: left;
  }

  canvas {
    max-height: 400px;
  }
}

.results-table {
  width: 100%;
  margin: 1rem 0;
  background: lighten($body-bg, 5%);
  border-radius: 0.5rem;
  color: $body-color;
  border: 1px solid rgba($body-color, 0.15);
  overflow: hidden;

  tr {
    border-bottom: 1px solid rgba($body-color, 0.1);

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: lighten($body-bg, 8%);
    }
  }

  td {
    padding: 1rem 1.5rem;
    vertical-align: top;
  }

  .input-prompt {
    font-weight: 600;
    color: $body-color;
    width: 30%;
  }

  .input-number {
    font-weight: 700;
    color: $primary;
    font-size: 1.1rem;
    width: 20%;
    text-align: right;
  }

  .input-help {
    color: rgba($body-color, 0.7);
    font-size: 0.9rem;
    width: 50%;
  }
}

.performance-content {
  text-align: left;
  padding: 1rem;

  h4 {
    font-size: 1.5rem;
    color: $body-color;
    margin-bottom: 1rem;
  }

  p, ul {
    font-size: 1rem;
    color: rgba($body-color, 0.9);
    margin-bottom: 1rem;
  }

  ul {
    padding-left: 1.5rem;
  }
}

// Responsive design
@media (min-width: 1200px) {
  .input-section {
    padding: 1.75rem;
  }
}

@media (max-width: 1199px) {
  .input-section {
    padding: 1.5rem;
  }
}

@media (max-width: 992px) {
  .input-section {
    padding: 1.25rem;
  }
}

@media (max-width: 991px) {
  .inputs-panel,
  .metrics-panel {
    height: auto;
    max-height: none;
  }

  .metrics-panel {
    border-left: none;
    border-top: 1px solid rgba($body-color, 0.1);
  }

  .inputs-panel,
  .metrics-panel {
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .calculator-main {
    height: calc(100vh - 80px);
    min-height: 400px;
  }

  .calculator-header {
    padding: 1.5rem 1rem 1rem 1rem;

    .calculator-title {
      font-size: 1.5rem;
    }
  }

  .main-tabs {
    ::v-deep .nav-tabs {
      padding: 0 1rem;
      .nav-item .nav-link {
        padding: 0.75rem 1rem;
        font-size: 1rem;
      }
    }
  }

  .inputs-panel,
  .metrics-panel {
    padding: 1rem;
  }

  .input-section {
    padding: 1rem;
    margin-bottom: 1rem;

    .section-title {
      font-size: 1rem;
    }
  }

  .form-field {
    margin-bottom: 1rem;

    .input-field {
      max-width: 100%;
    }
  }

  .key-results-section {
    .section-title {
      font-size: 1.1rem;
    }
  }

  .key-result-card {
    padding: 1rem;

    .key-result-value {
      font-size: 1.25rem;
    }
  }

  .chart-card-small,
  .savings-summary {
    margin-bottom: 1rem;
    padding: 1rem;
  }

  .results-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .result-card {
    padding: 1.5rem;

    .value {
      font-size: 1.75rem;
    }
  }

  .chart-card {
    padding: 1.5rem;

    canvas {
      max-height: 250px;
    }
  }

  .results-table {
    font-size: 0.9rem;

    td {
      padding: 0.75rem 1rem;
      display: block;
      width: 100% !important;
      text-align: left !important;

      &.input-number {
        font-weight: 700;
        font-size: 1.25rem;
        margin-top: 0.25rem;
      }
    }

    tr {
      border-bottom: 2px solid rgba($body-color, 0.2);
      padding: 1rem 0;
    }
  }
}

// Debug section styling - commented out for production (no on-screen debug data)
// .debug-section { ... }

// Analysis page styling
.analysis-container {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.hero-callout {
  background: linear-gradient(135deg, rgba($primary, 0.25) 0%, rgba($primary, 0.08) 100%);
  border-left: 4px solid $primary;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 0.375rem;

  .hero-callout-label {
    font-size: 0.9rem;
    color: rgba($body-color, 0.9);
    margin-bottom: 0.25rem;
  }

  .hero-callout-value {
    font-size: 2rem;
    font-weight: 800;
    color: $primary;
    line-height: 1.2;
  }

  .hero-callout-note {
    font-size: 0.85rem;
    color: rgba($body-color, 0.7);
    margin-top: 0.5rem;
  }
}

.sensitivity-hero .hero-chart {
  min-height: 320px;
}

.labor-traffic-light {
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  border: 2px solid;

  &.status-healthy {
    background: rgba($success, 0.15);
    border-color: $success;
  }

  &.status-borderline {
    background: rgba($warning, 0.15);
    border-color: $warning;
  }

  &.status-review {
    background: rgba($danger, 0.15);
    border-color: $danger;
  }

  .traffic-light-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: $body-color;
  }

  .traffic-light-status {
    font-size: 0.9rem;
    font-weight: 600;
    margin-top: 0.25rem;
  }

  .traffic-light-note {
    font-size: 0.85rem;
    color: rgba($body-color, 0.8);
    margin-top: 0.5rem;
  }
}

.analysis-section {
  margin-bottom: 3rem;
  
  .section-header {
    font-size: 1.5rem;
    font-weight: 700;
  }
  
  .download-pdf-section {
    text-align: center;
    padding: 2rem;
    background: lighten($body-bg, 3%);
    border: 1px solid rgba($body-color, 0.1);
    border-radius: 0.375rem;
    
    .download-pdf-btn {
      margin-top: 1rem;
      padding: 0.75rem 2rem;
      font-size: 1.1rem;
      font-weight: 600;
      
      i {
        margin-right: 0.5rem;
      }
    }
  }
  
  .section-description {
    color: rgba($body-color, 0.8);
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
    line-height: 1.6;
  }
}

.executive-summary {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($primary, 0.2);
  border-radius: 0.5rem;
  padding: 2rem;
  
  .section-disclaimer {
    color: rgba($body-color, 0.85);
    font-size: 0.9rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: rgba($body-color, 0.05);
    border-radius: 0.375rem;
    border-left: 4px solid rgba($primary, 0.2);
  }
  
  .summary-metric {
    text-align: center;
    padding: 1.5rem;
    background: lighten($body-bg, 3%);
    border-radius: 0.375rem;
    border: 1px solid rgba($body-color, 0.1);
    
    .metric-label {
      font-size: 0.95rem;
      font-weight: 600;
      color: rgba($body-color, 0.8);
      margin-bottom: 0.5rem;
    }
    
    .metric-value {
      font-size: 1.1rem;
      font-weight: 700;
      color: $body-color;
      margin-bottom: 0.25rem;
      
      &.highlight {
        color: $primary;
        font-size: 1.5rem;
      }
    }
    
    .metric-note {
      font-size: 0.75rem;
      color: rgba($body-color, 0.6);
    }
  }
}

.forecast-table-container {
  margin-top: 1.5rem;
  overflow-x: auto;
}

.forecast-table {
  width: 100%;
  border-collapse: collapse;
  background: lighten($body-bg, 3%);
  border-radius: 0.375rem;
  overflow: hidden;
  
  thead {
    background: lighten($body-bg, 5%);
    
    th {
      padding: 1rem;
      text-align: left;
      font-weight: 700;
      color: $primary;
      border-bottom: 2px solid rgba($primary, 0.3);
      font-size: 0.9rem;
    }
  }
  
  tbody {
    tr {
      border-bottom: 1px solid rgba($body-color, 0.1);
      
      &:last-child {
        border-bottom: none;
      }
      
      &:hover {
        background: lighten($body-bg, 2%);
      }
    }
    
    td {
      padding: 0.875rem 1rem;
      color: $body-color;
      font-size: 0.95rem;
      font-weight: 600;
      
      &:first-child {
        font-weight: 600;
      }
      
      &:not(:first-child) {
        text-align: right;
        font-family: monospace;
        font-size: 1.1rem;
        font-weight: 700;
      }
      
      &.positive {
        color: $success;
        font-weight: 700;
      }
      
      &.negative {
        color: $danger;
        font-weight: 700;
      }
    }
  }
}

.scenario-table-container {
  background: lighten($body-bg, 3%);
  border: 1px solid rgba($body-color, 0.1);
  border-radius: 0.375rem;
  padding: 1rem;
}

.scenario-table {
  width: 100%;
  border-collapse: collapse;
  
  thead {
    th {
      padding: 0.75rem;
      text-align: left;
      font-weight: 700;
      color: $primary;
      border-bottom: 2px solid rgba($primary, 0.3);
      font-size: 0.875rem;
    }
  }
  
  tbody {
    tr {
      border-bottom: 1px solid rgba($body-color, 0.1);
      
      &:last-child {
        border-bottom: none;
      }
      
      &:hover {
        background: lighten($body-bg, 2%);
      }
    }
    
    td {
      padding: 0.75rem;
      color: $body-color;
      font-size: 0.95rem;
      font-weight: 600;
      
      &:not(:first-child) {
        text-align: right;
        font-family: monospace;
        font-size: 1.1rem;
        font-weight: 700;
      }
    }
  }
}

.efficiency-card {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.1);
  border-radius: 0.375rem;
  padding: 1.5rem;
  height: 100%;
  
  h4 {
    font-size: 1.1rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba($primary, 0.2);
  }

  h5 {
    font-size: 1rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 0.5rem;
  }
}

.efficiency-table {
  width: 100%;
  
  tr {
    border-bottom: 1px solid rgba($body-color, 0.1);
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  td {
    padding: 0.75rem 0;
    color: $body-color;
    font-size: 0.95rem;
    font-weight: 600;
    
    &:first-child {
      font-weight: 600;
      color: rgba($body-color, 0.8);
    }
    
    &:last-child {
      text-align: right;
      font-family: monospace;
      font-size: 1.1rem;
      font-weight: 700;
    }
  }
}

.labor-note {
  font-size: 0.85rem;
  color: rgba($body-color, 0.8);
}

.labor-factors-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.9rem;
  color: rgba($body-color, 0.9);

  li {
    padding: 0.35rem 0;
    padding-left: 1rem;
    position: relative;

    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: $primary;
    }
  }
}

.labor-benchmarks {
  thead th {
    padding: 0.5rem 0;
    font-size: 0.875rem;
    color: $primary;
    border-bottom: 1px solid rgba($body-color, 0.2);
  }

  tbody td:last-child {
    font-family: inherit;
    font-weight: 600;
  }
}

.efficiency-table td.labor-good {
  color: $success;
  font-weight: 700;
}

.efficiency-table td.labor-warning {
  color: $warning;
  font-weight: 700;
}

.efficiency-table tr.metric-note-row td {
  font-size: 0.8rem;
  font-weight: normal;
  color: rgba($body-color, 0.65);
  padding-top: 0.25rem;
  padding-bottom: 0.5rem;
  font-style: italic;
}

.benefits-list {
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    padding: 0.75rem 0;
    border-bottom: 1px solid rgba($body-color, 0.1);
    color: $body-color;
    font-size: 0.9rem;
    line-height: 1.6;
    
    &:last-child {
      border-bottom: none;
    }
    
    strong {
      color: $primary;
      font-weight: 600;
    }
  }
}

.roi-breakdown-card {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.1);
  border-radius: 0.375rem;
  padding: 1.5rem;
  
  h4 {
    font-size: 1.1rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba($primary, 0.2);
  }
}

.roi-table {
  width: 100%;
  
  tr {
    border-bottom: 1px solid rgba($body-color, 0.1);
    
    &:last-child {
      border-bottom: none;
    }
    
    &.total-row {
      border-top: 2px solid rgba($primary, 0.3);
      border-bottom: 2px solid rgba($primary, 0.3);
      background: lighten($body-bg, 2%);
    }
  }
  
  td {
    padding: 0.75rem 0;
    color: $body-color;
    font-size: 0.95rem;
    font-weight: 600;
    
    &:first-child {
      font-weight: 600;
      color: rgba($body-color, 0.8);
    }
    
    &.value {
      text-align: right;
      font-family: monospace;
      font-size: 1.1rem;
      font-weight: 700;
      
      &.positive {
        color: $success;
      }
      
      &.highlight {
        color: $primary;
        font-size: 1.5rem;
        font-weight: 700;
      }
    }
  }
}

.recommendations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.recommendation-card {
  background: lighten($body-bg, 5%);
  border: 1px solid rgba($body-color, 0.1);
  border-radius: 0.375rem;
  padding: 1.5rem;
  
  h4 {
    font-size: 1.1rem;
    font-weight: 700;
    color: $primary;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba($primary, 0.2);
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      padding: 0.75rem 0;
      padding-left: 1.5rem;
      position: relative;
      color: $body-color;
      font-size: 0.9rem;
      line-height: 1.6;
      
      &:before {
        content: "→";
        position: absolute;
        left: 0;
        color: $primary;
        font-weight: 700;
      }
    }
  }
}

@media (max-width: 768px) {
  .analysis-container {
    padding: 1rem;
  }
  
  .analysis-section {
    margin-bottom: 2rem;
    
    .section-header {
      font-size: 1.25rem;
    }
  }
  
  .executive-summary {
    padding: 1.5rem;
    
    .summary-metric {
      margin-bottom: 1rem;
    }
  }
  
  .recommendations-grid {
    grid-template-columns: 1fr;
  }
}
</style>